import { nativeHorizontalCarousels } from 'src/components/carouselPolicy'
import { CSSProperties, ImgHTMLAttributes, ReactNode, useMemo, useState } from 'react'

/* Next */
import Link from 'src/components/Link'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

/* Material */
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

/* Material Icons */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import Replay30OutlinedIcon from '@mui/icons-material/Replay30Outlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined'

/* Parser */
import parse from 'html-react-parser'

/* Swiper */
import type { SwiperOptions } from 'swiper'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

/* Config */
import config from 'src/config'
import shopInfos from 'template/infos'
import magazineUrl from 'src/functions/magazineUrl'
import { getTemplateImageProps } from 'src/functions/templateImage'

/* Types */
import {
  MenuPageBuilder,
  MenuPageBuilderBannerBlock,
  MenuPageBuilderBannerItem,
  MenuPageBuilderBenefitsBlock,
  MenuPageBuilderBlock,
  MenuPageBuilderBrandListBlock,
  MenuPageBuilderCategoryListBlock,
  MenuPageBuilderCategorySliderBlock,
  MenuPageBuilderContactSubject,
  MenuPageBuilderButton,
  MenuPageBuilderExternalRel,
  MenuPageBuilderProductsBlock,
  MenuPageBuilderRichTextBlock,
} from 'src/shared/dbTypes/menuPageBuilder'

/* Components */
import ProductRow from 'src/components/ProductPreviewRenders/ProductRow'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import ContactModal from 'src/components/ContactModal/DeferredContactModal'
import { BreadcrumbTrail, SeoBreadcrumbJsonLd } from 'src/components/Breadcrumbs/Breadcrumbs'
import FilterTreeAccordion from 'src/layout/Navigation/FilterTree/FilterTreeAccordion'
import ProductsPagination from 'src/pages/Filter/Components/Products/Pagination'
import FilterTopBar from 'src/pages/Filter/Components/FilterTopBar/FilterTopBar'
import FilterStructuredData from 'src/pages/Filter/StructuredData'
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'
import type { item as NavigationItem } from 'src/shared/frontendTypes/navigation'

/* Redux */
import { useDispatch, useSelector } from 'src/redux/hooks'
import { shallowEqual } from 'react-redux'

/* Helpers */
import { resolvePageBuilder } from './config'
import { hasPageProductSelection, resolveProductBlockSelection } from './productBlockSelection'
import ResponsiveImage from './ResponsiveImage'
import BrandLogoOverlay, { hasBrandLogoOverlay } from './BrandLogoOverlay'
import NativeHorizontalSlider, { NativeSliderSlide } from 'src/components/NativeHorizontalSlider'
import Seo from 'src/components/Seo'
import { seoDescription, seoText } from 'src/functions/seo'
import {
  getExternalLinkRel,
  isAnchorOnlyHref,
  isExternalHref,
} from 'src/functions/linkPolicy'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

// Keep genuine sliders server-rendered; native and single-image sections do not load Swiper.
const Swiper = dynamic(() => import('src/components/SwiperSlider'))
const SwiperSlide = NativeSliderSlide

type ShopInfosWithReviews = typeof shopInfos & {
  googleRating?: string | number
  googleReviewCount?: string | number
  googleReviewHref?: string
  googlePlaceId?: string
  googlePlaceQuery?: string
}

const shopReviewInfos = shopInfos as ShopInfosWithReviews

interface PageBuilderLinkProps {
  href: string
  className?: string
  externalRel?: MenuPageBuilderExternalRel
  children: ReactNode
}

const PageBuilderLink: React.FC<PageBuilderLinkProps> = ({
  href,
  className,
  externalRel,
  children,
}) => {
  if (isAnchorOnlyHref(href)) {
    return <a
      href={href}
      className={className}
      target={isExternalHref(href) ? '_blank' : undefined}
      rel={getExternalLinkRel(href, externalRel)}
    >
      {children}
    </a>
  }

  return <Link href={href} passHref legacyBehavior>
    <a className={className}>{children}</a>
  </Link>
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    desktopOnly: { [theme.breakpoints.down('md')]: { display: 'none !important' } },
    mobileOnly: { [theme.breakpoints.up('md')]: { display: 'none !important' } },
    page: {
      backgroundColor: 'var(--page-builder-background, #f6f1ea)',
      color: 'var(--page-builder-text, #1f2933)',
      fontFamily: 'var(--page-builder-font-family, inherit)',
      overflowX: 'hidden',
      ...(nativeHorizontalCarousels ? {
        // Unlike hidden, clip does not create another scroll container on iOS.
        '@supports (overflow: clip)': { overflowX: 'clip' },
        overscrollBehaviorX: 'none',
      } : {}),
    },
    hero: {
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-end',
      color: '#ffffff',
      minHeight: 'var(--hero-desktop-height, 620px)',
      [theme.breakpoints.down('md')]: {
        minHeight: 'var(--hero-mobile-height, calc(100svh - 64px))',
      },
      '&.align-center': {
        textAlign: 'center',
        alignItems: 'center',
      },
    },
    heroOverlay: {
      position: 'absolute',
      inset: 0,
      zIndex: 1,
    },
    heroImage: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
    },
    heroContent: {
      position: 'relative',
      width: '100%',
      zIndex: 1,
      paddingTop: theme.spacing(10),
      paddingBottom: theme.spacing(8),
    },
    heroInner: {
      display: 'grid',
      gap: theme.spacing(2.25),
      maxWidth: 680,
      '&.align-center': {
        margin: '0 auto',
      },
    },
    eyebrow: {
      letterSpacing: '.18em',
      textTransform: 'uppercase',
      fontSize: '.8rem',
      opacity: .9,
    },
    heroTitle: {
      fontWeight: 700,
      lineHeight: 1,
      [theme.breakpoints.up('md')]: {
        fontSize: 'clamp(3.2rem, 7vw, 6rem)',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: 'clamp(2.4rem, 12vw, 4rem)',
      },
    },
    heroBrandTextTitle: {
      fontWeight: 800,
      lineHeight: 1.18,
      letterSpacing: 0,
      maxWidth: 980,
      [theme.breakpoints.up('md')]: {
        fontSize: 'clamp(1.35rem, 2.4vw, 2.05rem)',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.45rem',
      },
    },
    heroBody: {
      maxWidth: 560,
      fontSize: '1.05rem',
      lineHeight: 1.7,
      color: 'rgba(255,255,255,.92)',
      '&.align-center': {
        margin: '0 auto',
      },
    },
    heroActions: {
      display: 'flex',
      gap: theme.spacing(1.25),
      flexWrap: 'wrap',
      '&.align-center': {
        justifyContent: 'center',
      },
    },
    heroSplitSection: {
      backgroundColor: 'var(--page-builder-background, #f6f1ea)',
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(3),
      },
    },
    heroSplit: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, .95fr)',
      minHeight: 'var(--page-builder-hero-split-height, 340px)',
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
        minHeight: 'auto',
      },
    },
    heroSplitWithoutImage: {
      gridTemplateColumns: '1fr',
      minHeight: 'auto',
    },
    heroSplitText: {
      alignContent: 'start',
      display: 'grid',
      gap: theme.spacing(1.4),
      justifyItems: 'start',
      padding: theme.spacing(0, 0, 3.5, 4),
      textAlign: 'left',
      '&.align-center': {
        justifyItems: 'center',
        textAlign: 'center',
      },
      [theme.breakpoints.up('md')]: {
        '&.align-center': {
          justifyItems: 'start',
          textAlign: 'left',
        },
      },
      [theme.breakpoints.down('md')]: {
        order: 2,
        padding: theme.spacing(1, 0, 2.5),
      },
    },
    heroSplitTitle: {
      fontWeight: 700,
      lineHeight: 1.08,
      [theme.breakpoints.up('md')]: {
        fontSize: 'clamp(2.25rem, 4vw, 4.1rem)',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem',
      },
    },
    heroBrandLogo: {
      background: 'rgba(255, 255, 255, .92)',
      border: '1px solid rgba(15, 23, 42, .10)',
      maxHeight: 42,
      height: 42,
      maxWidth: 180,
      objectFit: 'contain',
      padding: theme.spacing(.5, .75),
      width: 'auto',
    },
    heroBrandLogoWrap: {
      display: 'inline-block',
      height: 42,
      lineHeight: 0,
      position: 'relative',
    },
    heroSplitBody: {
      color: 'var(--page-builder-muted, #52606d)',
      fontSize: '1rem',
      lineHeight: 1.65,
      maxWidth: 620,
    },
    heroSplitImageWrap: {
      minHeight: 'var(--page-builder-hero-split-height, 340px)',
      overflow: 'hidden',
      position: 'relative',
      [theme.breakpoints.down('md')]: {
        minHeight: 240,
        order: 1,
      },
    },
    heroSplitNaturalImageWrap: {
      alignSelf: 'start',
      minHeight: 0,
      [theme.breakpoints.down('md')]: {
        minHeight: 0,
      },
    },
    heroSplitImage: {
      height: '100%',
      inset: 0,
      objectFit: 'cover',
      objectPosition: 'center',
      position: 'absolute',
      width: '100%',
    },
    heroSplitNaturalImage: {
      display: 'block',
      height: 'auto',
      position: 'static',
      width: '100%',
    },
    heroSplitSecondaryButton: {
      borderColor: 'rgba(15, 23, 42, 0.22) !important',
      borderRadius: 999,
      color: 'var(--page-builder-text, #1f2933) !important',
      paddingBlock: theme.spacing(1.25),
      paddingInline: theme.spacing(3),
    },
    primaryButton: {
      backgroundColor: 'var(--page-builder-button-bg, var(--page-builder-accent, #b86f52)) !important',
      color: 'var(--page-builder-button-text, var(--page-builder-accent-contrast, #ffffff)) !important',
      borderRadius: 999,
      paddingInline: theme.spacing(3),
      paddingBlock: theme.spacing(1.25),
      '&:hover': {
        backgroundColor: 'var(--page-builder-button-hover, var(--page-builder-button-bg, #9f5f45)) !important',
      },
    },
    secondaryButton: {
      borderRadius: 999,
      paddingInline: theme.spacing(3),
      paddingBlock: theme.spacing(1.25),
      borderColor: 'rgba(255,255,255,.5) !important',
      color: '#ffffff !important',
    },
    section: {
      paddingTop: theme.spacing(7),
      paddingBottom: theme.spacing(7),
    },
    sectionInner: {
      display: 'grid',
      gap: theme.spacing(3),
    },
    legacyIntro: {
      paddingTop: theme.spacing(2.5),
      paddingBottom: theme.spacing(4),
    },
    legacyIntroInner: {
      display: 'grid',
      gap: theme.spacing(1.8),
    },
    legacyIntroBrandLogo: {
      maxHeight: 54,
      maxWidth: 220,
      objectFit: 'contain',
      width: 'auto',
      [theme.breakpoints.down('sm')]: {
        justifySelf: 'center',
        maxHeight: 44,
      },
    },
    legacyIntroTitle: {
      fontWeight: 500,
      lineHeight: 1.08,
      margin: 0,
      [theme.breakpoints.up('md')]: {
        fontSize: '2.6rem',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem',
      },
    },
    legacyIntroSeoTitle: {
      fontWeight: 800,
      lineHeight: 1.18,
      maxWidth: 980,
      [theme.breakpoints.up('md')]: {
        fontSize: 'clamp(1.35rem, 2.4vw, 2rem)',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.45rem',
      },
    },
    legacyIntroToggle: {
      justifySelf: 'flex-start',
      color: 'var(--page-builder-accent, #b86f52) !important',
      fontWeight: 700,
      letterSpacing: '.02em',
      paddingInline: 0,
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: 'transparent !important',
        color: 'var(--page-builder-button-hover, var(--page-builder-accent, #9f5f45)) !important',
      },
    },
    legacyIntroDivider: {
      border: 0,
      borderTop: '1px solid rgba(33, 28, 23, .34)',
      margin: theme.spacing(.75, 0, 0),
      width: '100%',
    },
    catalogLayout: {
      display: 'grid',
      gap: theme.spacing(4),
      alignItems: 'start',
    },
    catalogLayoutWithSidebar: {
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: '280px minmax(0, 1fr)',
      },
    },
    catalogSidebar: {
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'block',
      },
    },
    catalogSidebarInner: {
      backgroundColor: 'var(--page-builder-surface, #ffffff)',
      border: '1px solid rgba(15, 23, 42, 0.1)',
      minHeight: 260,
    },
    filterSidebarTitle: {
      alignItems: 'center',
      borderBottom: '1px solid rgba(15, 23, 42, 0.1)',
      display: 'flex',
      fontSize: '.86rem',
      fontWeight: 800,
      letterSpacing: '.04em',
      margin: 0,
      minHeight: 52,
      padding: theme.spacing(0, 2),
      textTransform: 'uppercase',
    },
    catalogProducts: {
      minWidth: 0,
    },
    catalogToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start',
        flexDirection: 'column',
      },
    },
    catalogCount: {
      margin: 0,
      fontSize: '.84rem',
      fontWeight: 700,
      letterSpacing: '.05em',
      textTransform: 'uppercase',
    },
    catalogMobileFilterButton: {
      borderColor: 'rgba(15, 23, 42, 0.18) !important',
      color: 'var(--page-builder-text, #1f2933) !important',
      [theme.breakpoints.up('md')]: {
        display: 'none !important',
      },
    },
    magazineFeature: {
      borderBottom: '1px solid rgba(49, 39, 28, .12)',
      borderTop: '1px solid rgba(49, 39, 28, .12)',
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(3),
      padding: theme.spacing(2.2, 0),
      position: 'relative',
    },
    magazineFeatureHeader: {
      alignItems: 'baseline',
      display: 'flex',
      gap: theme.spacing(2),
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1.4),
      position: 'relative',
      zIndex: 1,
      '& span': {
        color: '#83745e',
        fontSize: '.78rem',
        fontWeight: 800,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
      },
      '& h2': {
        color: '#211c17',
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: 'clamp(1.25rem, 2vw, 1.8rem)',
        lineHeight: 1,
        margin: 0,
      },
      [theme.breakpoints.down('sm')]: {
        alignItems: 'start',
        flexDirection: 'column',
      },
    },
    magazineFeatureGrid: {
      display: 'grid',
      gap: theme.spacing(1.5),
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      position: 'relative',
      zIndex: 1,
    },
    magazineFeatureCard: {
      alignItems: 'center',
      color: 'inherit',
      display: 'grid',
      gap: theme.spacing(1.4),
      gridTemplateColumns: '92px 1fr auto',
      minHeight: 116,
      padding: theme.spacing(.8, 0),
      textDecoration: 'none',
      transition: 'transform .18s ease, box-shadow .18s ease',
      '&::after': {
        alignItems: 'center',
        background: 'var(--page-builder-button-bg, #211c17)',
        borderRadius: 999,
        color: 'var(--page-builder-button-text, #fff)',
        content: '"Lesen"',
        display: 'inline-flex',
        fontSize: '.78rem',
        fontWeight: 800,
        height: 34,
        justifyContent: 'center',
        letterSpacing: '.04em',
        padding: theme.spacing(0, 1.5),
        textTransform: 'uppercase',
      },
      '&:hover': {
        transform: 'translateX(4px)',
      },
      '&:hover $magazineFeatureCover': {
        transform: 'rotate(-1.5deg) scale(1.02)',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '86px 1fr',
        '&::after': {
          gridColumn: '2',
          justifySelf: 'start',
          marginTop: theme.spacing(.4),
        },
      },
    },
    magazineFeatureCover: {
      aspectRatio: '3 / 4',
      background: '#e6dfd2',
      boxShadow: '0 14px 30px rgba(49, 39, 28, .18)',
      display: 'grid',
      overflow: 'hidden',
      placeItems: 'center',
      position: 'relative',
      transition: 'transform .18s ease',
      '& img': {
        height: '100%',
        objectFit: 'cover',
        width: '100%',
      },
      '& span': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '.95rem',
        padding: theme.spacing(1),
        textAlign: 'center',
      },
    },
    magazineFeatureText: {
      alignContent: 'center',
      display: 'grid',
      gap: theme.spacing(.35),
      minWidth: 0,
      '& small': {
        color: '#83745e',
        fontWeight: 800,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
      },
      '& strong': {
        color: '#211c17',
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1.16rem',
        lineHeight: 1.04,
      },
      '& span': {
        color: 'rgba(33, 31, 28, .68)',
        fontSize: '.9rem',
      },
    },
    breadcrumbWrap: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
    sectionHeader: {
      display: 'grid',
      gap: theme.spacing(1),
      width: '100%',
      maxWidth: 'none',
    },
    sectionHeaderRow: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start',
        flexDirection: 'column',
      },
    },
    sectionActionLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(.8),
      color: 'var(--page-builder-text, #211c17)',
      fontSize: '.84rem',
      fontWeight: 700,
      textDecoration: 'none',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: 'var(--page-builder-accent, #6b5948)',
      },
    },
    subtitle: {
      color: 'var(--page-builder-muted, #52606d)',
    },
    banner: {
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-end',
      overflow: 'hidden',
      borderRadius: 28,
      minHeight: 380,
      color: '#ffffff',
      '&.fixed-height': {
        height: 'var(--page-builder-banner-height, 420px)',
        minHeight: 'var(--page-builder-banner-height, 420px)',
      },
      '&.mobile-compact-text': {
        [theme.breakpoints.down('md')]: {
          height: 'min(720px, 82svh)',
          minHeight: 'min(720px, 82svh)',
          borderRadius: 28,
        },
      },
    },
    bannerStrip: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      overflow: 'hidden',
      color: 'var(--page-builder-nav-text, #1f2933)',
      backgroundColor: 'rgba(255,255,255,0.78)',
      backdropFilter: 'blur(18px) saturate(180%)',
      WebkitBackdropFilter: 'blur(18px) saturate(180%)',
      [theme.breakpoints.down('md')]: {
        minHeight: 42,
      },
    },
    bannerOverlay: {
      position: 'absolute',
      inset: 0,
      zIndex: 1,
      backgroundColor: 'var(--banner-overlay-color, #101828)',
      opacity: 'var(--banner-overlay-opacity, .32)',
    },
    bannerCompactOverlay: {
      [theme.breakpoints.down('md')]: {
        background: 'var(--banner-mobile-overlay, transparent)',
        opacity: 1,
      },
    },
    bannerImage: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
      '&.mobile-compact-image': {
        [theme.breakpoints.down('md')]: {
          objectPosition: '62% center',
        },
      },
    },
    bannerStripContent: {
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1.25),
      width: '100%',
      padding: theme.spacing(1, 2),
      textAlign: 'center',
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(.9, 1.5),
      },
    },
    bannerStripText: {
      fontSize: '.95rem',
      fontWeight: 500,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      [theme.breakpoints.down('md')]: {
        fontSize: '.82rem',
      },
    },
    bannerStripLink: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'inherit',
      flexWrap: 'wrap',
      gap: theme.spacing(.9),
      color: 'inherit',
      textDecoration: 'none',
      '&:hover': {
        opacity: .82,
      },
    },
    bannerContent: {
      position: 'relative',
      zIndex: 1,
      display: 'grid',
      gap: theme.spacing(1.5),
      padding: theme.spacing(4),
      maxWidth: 560,
      '&.align-center': {
        margin: '0 auto',
        textAlign: 'center',
      },
      '&.align-right': {
        marginLeft: 'auto',
        textAlign: 'right',
      },
      '&.mobile-compact-text': {
        [theme.breakpoints.down('md')]: {
          maxWidth: 'min(430px, 64%)',
          padding: theme.spacing(4.5, 3, 4),
          gap: theme.spacing(1.1),
          '& $eyebrow': {
            fontSize: '.72rem',
            letterSpacing: '.2em',
          },
          '& h2': {
            fontSize: 'clamp(1.9rem, 8vw, 2.9rem)',
            lineHeight: 1.1,
          },
          '& p': {
            fontSize: 'clamp(.9rem, 3.7vw, 1.05rem)',
            lineHeight: 1.55,
          },
          '& .MuiButton-root': {
            width: 'fit-content',
            minWidth: 190,
            fontSize: '.88rem',
            paddingInline: theme.spacing(2.25),
            paddingBlock: theme.spacing(.95),
          },
        },
      },
      '&.mobile-text-small': {
        [theme.breakpoints.down('md')]: {
          '& $eyebrow': {
            fontSize: '.68rem',
            letterSpacing: '.18em',
          },
          '& h2': {
            fontSize: 'clamp(1.45rem, 6.4vw, 2.25rem)',
            lineHeight: 1.14,
          },
          '& p': {
            fontSize: 'clamp(.78rem, 3.2vw, .95rem)',
            lineHeight: 1.55,
          },
          '& .MuiButton-root': {
            fontSize: '.82rem',
            paddingInline: theme.spacing(2),
            paddingBlock: theme.spacing(.85),
          },
        },
      },
      '&.mobile-text-shadow': {
        [theme.breakpoints.down('md')]: {
          textShadow: [
            '0 1px 2px rgba(0,0,0,.86)',
            '0 5px 16px rgba(0,0,0,.68)',
            '0 18px 42px rgba(0,0,0,.58)',
          ].join(', '),
          '& .MuiTypography-root': {
            textShadow: [
              '0 1px 2px rgba(0,0,0,.86)',
              '0 5px 16px rgba(0,0,0,.68)',
              '0 18px 42px rgba(0,0,0,.58)',
            ].join(', ') + ' !important',
            filter: 'drop-shadow(0 8px 18px rgba(0,0,0,.42))',
          },
        },
      },
    },
    splitBanner: {
      display: 'grid',
      gridTemplateColumns: 'minmax(320px, .42fr) minmax(0, .58fr)',
      minHeight: 320,
      overflow: 'hidden',
      backgroundColor: 'var(--page-builder-surface, #f8f4ef)',
      boxShadow: '0 18px 46px rgba(42, 33, 26, 0.06)',
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
      },
    },
    splitBannerPanel: {
      display: 'grid',
      alignContent: 'center',
      gap: theme.spacing(1.7),
      padding: theme.spacing(4.5, 5),
      color: 'var(--page-builder-text, #211c17)',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3.5, 2.5),
      },
    },
    splitBannerEyebrow: {
      color: 'var(--page-builder-muted, #6d6257)',
      fontSize: '.78rem',
      fontWeight: 700,
      letterSpacing: '.16em',
      textTransform: 'uppercase',
    },
    splitBannerBody: {
      maxWidth: 430,
      color: 'var(--page-builder-muted, #6d6257)',
      lineHeight: 1.65,
      whiteSpace: 'pre-line',
    },
    splitBannerActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.25),
      marginTop: theme.spacing(.7),
    },
    splitPrimaryButton: {
      borderRadius: 4,
      backgroundColor: 'var(--page-builder-button-bg, var(--page-builder-accent, #6b5948)) !important',
      color: 'var(--page-builder-button-text, var(--page-builder-accent-contrast, #ffffff)) !important',
      paddingInline: theme.spacing(2.4),
      paddingBlock: theme.spacing(1),
      boxShadow: '0 12px 24px rgba(42, 33, 26, 0.14)',
      '&:hover': {
        backgroundColor: 'var(--page-builder-button-hover, var(--page-builder-button-bg, #5d4c3d)) !important',
      },
    },
    splitSecondaryButton: {
      borderRadius: 4,
      backgroundColor: '#ffffff !important',
      borderColor: 'rgba(42,33,26,.18) !important',
      color: 'var(--page-builder-text, #211c17) !important',
      paddingInline: theme.spacing(2.4),
      paddingBlock: theme.spacing(1),
    },
    splitBannerImageWrap: {
      position: 'relative',
      minHeight: 320,
      [theme.breakpoints.down('md')]: {
        minHeight: 280,
      },
    },
    splitBannerImage: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
    },
    splitBannerImageShade: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(90deg, rgba(21,15,10,.68) 0%, rgba(21,15,10,.44) 34%, rgba(21,15,10,.12) 62%, rgba(21,15,10,0) 100%)',
    },
    splitBannerImageContent: {
      position: 'absolute',
      left: theme.spacing(4.5),
      right: theme.spacing(4),
      bottom: theme.spacing(4),
      zIndex: 1,
      display: 'grid',
      gap: theme.spacing(1.1),
      maxWidth: 720,
      color: '#fff',
      textShadow: '0 2px 14px rgba(0,0,0,.55)',
      [theme.breakpoints.down('sm')]: {
        left: theme.spacing(2.5),
        right: theme.spacing(2.5),
        bottom: theme.spacing(2.5),
      },
    },
    storyBanner: {
      display: 'grid',
      overflow: 'hidden',
      backgroundColor: 'var(--page-builder-surface, #f8f4ef)',
      boxShadow: '0 18px 46px rgba(42, 33, 26, 0.06)',
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'minmax(320px, .38fr) minmax(0, .62fr)',
        minHeight: 360,
      },
      [theme.breakpoints.down('md')]: {
        borderRadius: 28,
      },
    },
    storyIntro: {
      display: 'grid',
      alignContent: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(4.8, 5),
      color: 'var(--page-builder-text, #211c17)',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3.8, 2.6, 2.4),
      },
    },
    storyBody: {
      maxWidth: 460,
      color: 'var(--page-builder-muted, #6d6257)',
      lineHeight: 1.65,
      whiteSpace: 'pre-line',
    },
    storyImageWrap: {
      position: 'relative',
      minHeight: 360,
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: {
        minHeight: 410,
      },
    },
    storyImage: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
    },
    storyImageShade: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(180deg, rgba(21,15,10,0) 35%, rgba(21,15,10,.72) 100%)',
    },
    storyImageContent: {
      position: 'absolute',
      left: theme.spacing(3),
      right: theme.spacing(3),
      bottom: theme.spacing(3),
      zIndex: 1,
      display: 'grid',
      gap: theme.spacing(1.15),
      maxWidth: 520,
      color: '#fff',
      textShadow: '0 2px 12px rgba(0,0,0,.5)',
      [theme.breakpoints.down('sm')]: {
        left: theme.spacing(2.4),
        right: theme.spacing(2.4),
        bottom: theme.spacing(2.4),
      },
    },
    storyImageActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.1),
      marginTop: theme.spacing(.4),
    },
    bannerSlider: {
      position: 'relative',
      isolation: 'isolate',
      overscrollBehaviorX: 'contain',
      '& .swiper': {
        overflow: 'hidden',
        borderRadius: 20,
        backgroundColor: 'var(--page-builder-surface, #fffaf6)',
        boxShadow: '0 14px 36px rgba(42, 33, 26, 0.05)',
        scrollbarWidth: 'none',
        touchAction: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        [theme.breakpoints.down('sm')]: {
          borderRadius: 16,
        },
      },
      '& .swiper-wrapper': {
        alignItems: 'stretch',
        transitionTimingFunction: 'cubic-bezier(.22, .61, .36, 1)',
        willChange: 'transform',
      },
      '& .swiper-slide': {
        backfaceVisibility: 'hidden',
        boxSizing: 'border-box',
        flexShrink: 0,
        height: 'auto',
        overflow: 'hidden',
        transform: 'translateZ(0)',
      },
      '& .swiper-slide > div': {
        height: '100%',
      },
      '& $banner, & $storyBanner, & $splitBanner': {
        borderRadius: 0,
        boxShadow: 'none',
      },
      '& $bannerImage, & $storyImage, & $splitBannerImage': {
        transform: 'translateZ(0) scale(1.0001)',
        willChange: 'opacity, transform',
      },
      '& .swiper-button-prev, & .swiper-button-next': {
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        backgroundColor: 'rgba(255,255,255,.82)',
        color: 'var(--page-builder-text, #1f2933)',
        boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(10px)',
        opacity: .72,
        transition: 'background-color .18s ease, opacity .18s ease',
        zIndex: 4,
        '&:after': {
          fontSize: 14,
          fontWeight: 700,
        },
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,.94)',
          opacity: 1,
        },
        [theme.breakpoints.down('sm')]: {
          display: 'flex',
          width: 44,
          height: 44,
          borderColor: 'rgba(15, 23, 42, 0.12)',
          backgroundColor: 'rgba(255,255,255,.96)',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.18)',
          color: 'var(--page-builder-text, #211c17)',
          opacity: .98,
          '&:after': {
            fontSize: 16,
          },
        },
      },
      '& .swiper-pagination': {
        left: '50%',
        bottom: 12,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        width: 'auto !important',
        padding: theme.spacing(.55, .75),
        borderRadius: 999,
        backgroundColor: 'rgba(15, 23, 42, 0.28)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        transform: 'translateX(-50%)',
        [theme.breakpoints.down('sm')]: {
          bottom: 9,
          padding: theme.spacing(.45, .6),
        },
      },
      '& .swiper-pagination-bullet': {
        width: 7,
        height: 7,
        margin: '0 !important',
        backgroundColor: '#ffffff',
        opacity: .48,
        transition: 'opacity .18s ease, transform .18s ease, width .18s ease',
      },
      '& .swiper-pagination-bullet-active': {
        width: 18,
        borderRadius: 999,
        opacity: 1,
      },
      '& .swiper-button-disabled': {
        opacity: .22,
      },
      '& .swiper-button-prev': {
        left: 12,
        [theme.breakpoints.down('sm')]: {
          left: 12,
        },
      },
      '& .swiper-button-next': {
        right: 12,
        [theme.breakpoints.down('sm')]: {
          right: 12,
        },
      },
    },
    bannerSliderImageControls: {
      [theme.breakpoints.down('sm')]: {
        '& .swiper-button-prev, & .swiper-button-next': {
          top: 'calc(100% - var(--page-builder-banner-mobile-arrow-offset, 205px))',
          marginTop: 0,
          transform: 'translateY(-50%)',
        },
      },
    },
    linkGrid: {
      display: 'grid',
      gap: theme.spacing(2),
      gridTemplateColumns: 'var(--link-grid-columns)',
      justifyContent: 'var(--link-grid-alignment, center)',
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(var(--link-grid-mobile-columns, 1), minmax(0, 1fr))',
        justifyContent: 'normal',
      },
    },
    brandGrid: {
      display: 'grid',
      gap: theme.spacing(2.25),
      gridTemplateColumns: 'var(--brand-grid-columns)',
      justifyContent: 'center',
      alignItems: 'start',
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        justifyContent: 'normal',
      },
    },
    linkSliderRoot: {
      position: 'relative',
      overflow: 'hidden',
      overscrollBehaviorX: 'contain',
      paddingInline: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        paddingInline: 0,
      },
      '& .swiper': {
        overflow: 'hidden',
        overflowY: 'hidden !important',
        overscrollBehaviorY: 'none',
        scrollbarWidth: 'none',
        touchAction: 'pan-y pinch-zoom',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
      '& .swiper-wrapper': {
        alignItems: 'center',
        willChange: 'transform',
      },
      '& .swiper-slide': {
        alignItems: 'center',
        backfaceVisibility: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexShrink: 0,
        height: 'auto',
        overflow: 'hidden',
        transform: 'translateZ(0)',
        width: 'var(--page-builder-link-slide-width, 240px)',
        [theme.breakpoints.down('sm')]: {
          width: 'var(--page-builder-link-slide-width-mobile, 220px)',
        },
      },
      '& .swiper-slide > a': {
        height: '100%',
        width: '100%',
      },
      '& .swiper-button-prev, & .swiper-button-next': {
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        backgroundColor: 'rgba(255,255,255,.9)',
        color: 'var(--page-builder-text, #1f2933)',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(10px)',
        transition: 'opacity .18s ease, background-color .18s ease, transform .18s ease',
        '&:after': {
          fontSize: 12,
          fontWeight: 700,
        },
        '&:hover': {
          backgroundColor: '#ffffff',
          transform: 'translateY(-1px)',
        },
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      },
      '& .swiper-button-prev': {
        left: 0,
      },
      '& .swiper-button-next': {
        right: 0,
      },
    },
    linkCard: {
      position: 'relative',
      display: 'block',
      overflow: 'hidden',
      minHeight: 240,
      borderRadius: 24,
      backgroundColor: 'var(--page-builder-surface, #ffffff)',
      boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)',
      color: 'inherit',
      textDecoration: 'none',
      '& *': {
        color: 'inherit',
        textDecoration: 'none !important',
      },
      '&.shape-square': {
        borderRadius: 0,
      },
      '&.shape-rounded': {
        borderRadius: 24,
      },
      '&.shape-round': {
        borderRadius: '50%',
        aspectRatio: '1 / 1',
        minHeight: 'unset',
        width: '100%',
        maxWidth: 126,
        marginInline: 'auto',
      },
      '&.shape-oval': {
        borderRadius: 999,
        minHeight: 188,
        maxWidth: 300,
        marginInline: 'auto',
      },
      '&.size-small': {
        minHeight: 116,
        maxWidth: 148,
        '&.shape-round': {
          maxWidth: 84,
        },
        '&.shape-oval': {
          minHeight: 88,
          maxWidth: 160,
        },
        '&.shape-rounded': {
          minHeight: 140,
          maxWidth: 168,
        },
      },
      '&.size-large': {
        minHeight: 360,
        maxWidth: 520,
        '&.shape-round': {
          maxWidth: 184,
        },
        '&.shape-oval': {
          minHeight: 230,
          maxWidth: 360,
        },
        '&.shape-rounded': {
          minHeight: 400,
        },
      },
      '&.shadow-none': {
        boxShadow: 'none',
      },
      '&.shadow-soft': {
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
      },
      '&.shadow-medium': {
        boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
      },
    },
    brandCard: {
      display: 'grid',
      alignItems: 'center',
      justifyItems: 'center',
      padding: theme.spacing(1.6),
      textAlign: 'center',
      color: 'inherit',
      textDecoration: 'none',
      '& *': {
        color: 'inherit',
        textDecoration: 'none !important',
      },
      backgroundColor: 'var(--page-builder-surface, #ffffff)',
      width: '100%',
      justifySelf: 'center',
      maxWidth: 200,
      minHeight: 124,
      boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
      '&.shape-square': {
        borderRadius: 0,
        width: '100%',
        maxWidth: '100%',
        minHeight: 124,
      },
      '&.shape-rounded': {
        borderRadius: 24,
        width: '100%',
        maxWidth: 216,
        minHeight: 144,
      },
      '&.shape-round': {
        borderRadius: '50% !important',
        width: 88,
        minWidth: 88,
        minHeight: 88,
        height: 88,
        padding: theme.spacing(.8),
      },
      '&.shape-oval': {
        borderRadius: 999,
        width: '100%',
        maxWidth: 196,
        minHeight: 96,
      },
      '&.size-small': {
        width: '100%',
        maxWidth: 108,
        minHeight: 72,
        padding: theme.spacing(.65),
        '&.shape-round': {
          width: 60,
          minWidth: 60,
          minHeight: 60,
          height: 60,
          padding: theme.spacing(.45),
        },
        '&.shape-oval': {
          maxWidth: 124,
          minHeight: 58,
        },
        '&.shape-rounded': {
          maxWidth: 122,
          minHeight: 72,
        },
      },
      '&.size-large': {
        width: '100%',
        maxWidth: 248,
        minHeight: 156,
        padding: theme.spacing(2.6),
        '&.shape-round': {
          width: 120,
          minWidth: 120,
          minHeight: 120,
          height: 120,
        },
        '&.shape-oval': {
          maxWidth: 228,
          minHeight: 108,
        },
        '&.shape-rounded': {
          maxWidth: 248,
          minHeight: 160,
        },
      },
      '&.shadow-none': {
        boxShadow: 'none',
      },
      '&.shadow-soft': {
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
      },
      '&.shadow-medium': {
        boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
      },
    },
    brandCardImageWrap: {
      width: '100%',
      display: 'grid',
      placeItems: 'center',
      minHeight: 36,
      overflow: 'hidden',
      position: 'relative',
    },
    brandCardImage: {
      width: '100%',
      maxWidth: 116,
      maxHeight: 48,
      objectFit: 'contain',
      display: 'block',
      transform: 'translate(var(--page-builder-brand-logo-offset-x, 0px), var(--page-builder-brand-logo-offset-y, 0px))',
      transition: 'transform .16s ease',
    },
    brandCardLabel: {
      marginTop: theme.spacing(1),
      fontWeight: 600,
      fontSize: '.92rem',
    },
    linkCardImage: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
    },
    linkCardContent: {
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      minHeight: '100%',
      padding: theme.spacing(3),
      color: '#ffffff',
      textDecoration: 'none',
      textShadow: '0 2px 10px rgba(0,0,0,.45)',
      background: 'linear-gradient(180deg, rgba(15,23,42,0.02) 34%, rgba(15,23,42,0.76) 100%)',
      '&.plain': {
        background: 'none',
        color: 'inherit',
        textShadow: 'none',
      },
    },
    sliderRoot: {
      position: 'relative',
      overflow: 'hidden',
      overscrollBehaviorX: 'contain',
      paddingInline: theme.spacing(2.5),
      [theme.breakpoints.down('sm')]: {
        paddingInline: 0,
      },
      '& .swiper': {
        overflow: 'hidden',
        scrollbarWidth: 'none',
        touchAction: 'pan-y pinch-zoom',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
      '& .swiper-wrapper': {
        alignItems: 'stretch',
        willChange: 'transform',
      },
      '& .swiper-css-mode': {
        touchAction: 'pan-x pan-y pinch-zoom',
        '& > .swiper-wrapper': {
          overflowX: 'auto',
          overflowY: 'hidden',
          willChange: 'auto',
        },
      },
      '& .swiper-slide': {
        backfaceVisibility: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexShrink: 0,
        height: 'auto',
        justifyContent: 'center',
        transform: 'translateZ(0)',
        width: 'var(--page-builder-slider-slide-width, 172px)',
        [theme.breakpoints.down('sm')]: {
          width: 'var(--page-builder-slider-slide-width-mobile, 152px)',
        },
      },
      '& .swiper-slide > a': {
        height: '100%',
        width: '100%',
      },
      '&.stretch-items .swiper:not(.swiper-initialized) .swiper-slide': {
        width: 'var(--category-slide-width)',
        marginRight: 'var(--category-slide-gap)',
        '@media (min-width: 600px)': {
          width: 'var(--category-slide-width-sm)',
          marginRight: 'var(--category-slide-gap-sm)',
        },
        '@media (min-width: 960px)': {
          width: 'var(--category-slide-width-md)',
          marginRight: 'var(--category-slide-gap-md)',
        },
        '@media (min-width: 1280px)': {
          width: 'var(--category-slide-width-lg)',
          marginRight: 'var(--category-slide-gap-lg)',
        },
      },
      '& .swiper-button-prev, & .swiper-button-next': {
        width: 36,
        height: 36,
        marginTop: -18,
        borderRadius: '50%',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        backgroundColor: 'rgba(255,255,255,.9)',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(10px)',
        color: 'var(--page-builder-text, #1f2933)',
        transition: 'opacity .18s ease, background-color .18s ease, transform .18s ease',
        '&:after': {
          fontSize: 12,
          fontWeight: 700,
        },
        '&:hover': {
          backgroundColor: '#ffffff',
          transform: 'translateY(-1px)',
        },
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      },
      '& .swiper-button-prev': {
        left: 0,
      },
      '& .swiper-button-next': {
        right: 0,
      },
      '& .swiper-button-disabled': {
        opacity: .35,
      },
      '&.compact': {
        paddingInline: 0,
        '& .swiper-button-prev, & .swiper-button-next': {
          display: 'none',
        },
      },
    },
    sliderCardLink: {
      display: 'grid',
      gap: theme.spacing(2.5),
      justifyItems: 'center',
      width: '100%',
      color: 'inherit',
      textDecoration: 'none',
      textAlign: 'center',
      paddingBottom: theme.spacing(1),
    },
    sliderImageShell: {
      width: '100%',
      aspectRatio: '1 / 1',
      borderRadius: '50%',
      backgroundColor: 'var(--page-builder-category-image-bg, transparent)',
      boxShadow: 'none',
      display: 'grid',
      placeItems: 'center',
      overflow: 'hidden',
      position: 'relative',
      padding: 0,
      '&.size-small': {
        maxWidth: 96,
        marginInline: 'auto',
      },
      '&.size-medium': {
        maxWidth: 128,
        marginInline: 'auto',
      },
      '&.size-large': {
        maxWidth: 178,
        marginInline: 'auto',
      },
      '&.shape-square': {
        borderRadius: 0,
      },
      '&.shape-rounded': {
        borderRadius: 18,
      },
      '&.shape-round': {
        borderRadius: '50%',
      },
      '&.shape-oval': {
        aspectRatio: '1.45 / 1',
        borderRadius: 999,
      },
      '&.shadow-none': {
        boxShadow: 'none',
      },
      '&.shadow-soft': {
        boxShadow: '0 10px 26px rgba(15, 23, 42, 0.06), inset 0 0 0 1px rgba(255,255,255,.5)',
      },
      '&.shadow-medium': {
        boxShadow: '0 18px 42px rgba(15, 23, 42, 0.12), inset 0 0 0 1px rgba(255,255,255,.5)',
      },
      [theme.breakpoints.down('sm')]: {
        '&.size-small': {
          maxWidth: 84,
        },
        '&.size-medium': {
          maxWidth: 108,
        },
        '&.size-large': {
          maxWidth: 148,
        },
      },
    },
    sliderImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
    },
    sliderTextWrap: {
      display: 'grid',
      gap: theme.spacing(.75),
      width: '100%',
      minWidth: 0,
    },
    sliderLabel: {
      textTransform: 'none',
      letterSpacing: 0,
      fontWeight: 500,
      lineHeight: 1.4,
      overflowWrap: 'anywhere',
      [theme.breakpoints.up('md')]: {
        fontSize: '1.05rem',
      },
    },
    sliderSubtitle: {
      color: 'var(--page-builder-muted, #52606d)',
      lineHeight: 1.5,
      overflowWrap: 'anywhere',
    },
    sliderSlide: {},
    compactSliderLink: {
      display: 'grid',
      gap: theme.spacing(1.2),
      justifyItems: 'center',
      width: '100%',
      color: 'inherit',
      textDecoration: 'none',
      textAlign: 'center',
      paddingBottom: 0,
    },
    compactImageShell: {
      width: 96,
      height: 96,
      borderRadius: '50%',
      backgroundColor: 'var(--page-builder-category-image-bg, transparent)',
      border: '1px solid var(--page-builder-category-image-border, transparent)',
      display: 'grid',
      placeItems: 'center',
      overflow: 'hidden',
      position: 'relative',
      padding: 0,
      '&.size-small': {
        width: 82,
        height: 82,
      },
      '&.size-medium': {
        width: 96,
        height: 96,
      },
      '&.size-large': {
        width: 112,
        height: 112,
      },
      '&.shape-square': {
        borderRadius: 0,
      },
      '&.shape-rounded': {
        borderRadius: 12,
      },
      '&.shape-round': {
        borderRadius: '50%',
      },
      '&.shape-oval': {
        width: 128,
        borderRadius: 999,
      },
      '&.shape-oval.size-small': {
        width: 108,
      },
      '&.shape-oval.size-large': {
        width: 148,
      },
      '&.shadow-none': {
        boxShadow: 'none',
      },
      '&.shadow-soft': {
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
      },
      '&.shadow-medium': {
        boxShadow: '0 14px 28px rgba(15, 23, 42, 0.14)',
      },
      [theme.breakpoints.down('sm')]: {
        width: 86,
        height: 86,
        '&.size-small': {
          width: 76,
          height: 76,
        },
        '&.size-large': {
          width: 100,
          height: 100,
        },
      },
    },
    compactImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
    },
    compactLabel: {
      fontSize: '.88rem',
      fontWeight: 400,
      lineHeight: 1.35,
      overflowWrap: 'anywhere',
      [theme.breakpoints.up('md')]: {
        fontSize: '.9rem',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '.84rem',
      },
    },
    compactSlide: {},
    richText: {
      display: 'grid',
      gap: theme.spacing(2),
      width: '100%',
      maxWidth: 'none',
      fontFamily: 'inherit',
      position: 'relative',
      '&.narrow': {
        maxWidth: 720,
      },
      '&.center': {
        margin: '0 auto',
        textAlign: 'center',
        justifyItems: 'center',
      },
      '& p': {
        margin: 0,
        lineHeight: 1.8,
      },
      '& h1, & h2, & h3': {
        lineHeight: 1.2,
        margin: 0,
      },
      '&, & *': {
        fontFamily: 'inherit',
      },
      '&.collapsed': {
        maxHeight: 'var(--rich-text-collapsed-height, 10.8em)',
        overflow: 'hidden',
        '&:after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '4.5em',
          pointerEvents: 'none',
          background: 'linear-gradient(to bottom, rgba(255,255,255,0), var(--rich-text-fade-bg, var(--page-builder-background, #fff)) 84%)',
        },
      },
    },
    richTextToggle: {
      justifySelf: 'flex-start',
      borderRadius: 999,
      paddingInline: theme.spacing(2.5),
      textTransform: 'none',
      '&.center': {
        justifySelf: 'center',
      },
    },
    benefitsGrid: {
      display: 'grid',
      gap: theme.spacing(2),
      gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
      [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      [theme.breakpoints.up('lg')]: {
        gridTemplateColumns: 'repeat(var(--page-builder-benefit-columns, 3), minmax(0, 1fr))',
      },
      '&.strip': {
        alignItems: 'stretch',
        gap: 0,
        [theme.breakpoints.up('md')]: {
          gridTemplateColumns: 'repeat(var(--page-builder-benefit-columns, 3), minmax(0, 1fr))',
        },
      },
      '&.trust-bar': {
        alignItems: 'stretch',
        gap: 0,
        backgroundColor: 'var(--page-builder-surface, #fffaf6)',
        borderTop: '1px solid rgba(42, 33, 26, 0.1)',
        borderBottom: '1px solid rgba(42, 33, 26, 0.1)',
        [theme.breakpoints.up('md')]: {
          gridTemplateColumns: 'repeat(var(--page-builder-benefit-columns, 3), minmax(0, 1fr))',
        },
      },
      '&.review-grid': {
        alignItems: 'stretch',
        gap: 0,
        backgroundColor: 'var(--page-builder-surface, #fffaf6)',
        borderTop: '1px solid rgba(42, 33, 26, 0.1)',
        borderBottom: '1px solid rgba(42, 33, 26, 0.1)',
        gridTemplateColumns: 'repeat(var(--page-builder-benefit-columns, 3), minmax(0, 1fr))',
      },
    },
    benefitCard: {
      alignItems: 'flex-start',
      backgroundColor: 'var(--page-builder-surface, #ffffff)',
      border: '1px solid rgba(15, 23, 42, 0.08)',
      borderRadius: 18,
      boxSizing: 'border-box',
      color: 'inherit',
      display: 'grid',
      gap: theme.spacing(1.2),
      minHeight: 154,
      padding: theme.spacing(3),
      textDecoration: 'none',
      '&.strip': {
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        borderRight: '1px solid rgba(42, 33, 26, 0.14)',
        borderRadius: 0,
        justifyItems: 'center',
        minHeight: 152,
        padding: theme.spacing(3, 2.5),
        textAlign: 'center',
        '&:last-child': {
          borderRight: 0,
        },
        [theme.breakpoints.down('sm')]: {
          borderRight: 0,
          borderBottom: '1px solid rgba(42, 33, 26, 0.12)',
          '&:last-child': {
            borderBottom: 0,
          },
        },
      },
      '&.trust-bar': {
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        borderRight: '1px solid rgba(42, 33, 26, 0.12)',
        borderRadius: 0,
        justifyItems: 'center',
        minHeight: 128,
        padding: theme.spacing(2.8, 2.2),
        textAlign: 'center',
        '&:last-child': {
          borderRight: 0,
        },
        [theme.breakpoints.down('sm')]: {
          borderRight: 0,
          borderBottom: '1px solid rgba(42, 33, 26, 0.1)',
          '&:last-child': {
            borderBottom: 0,
          },
        },
      },
      '&.review-grid': {
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        borderRight: '1px solid rgba(42, 33, 26, 0.12)',
        borderRadius: 0,
        justifyItems: 'center',
        minHeight: 136,
        padding: theme.spacing(2.4, 2),
        textAlign: 'center',
        '&:nth-child(3n)': {
          borderRight: 0,
        },
        '&:nth-child(n + 4)': {
          borderTop: '1px solid rgba(42, 33, 26, 0.08)',
        },
        [theme.breakpoints.down('sm')]: {
          minHeight: 118,
          padding: theme.spacing(1.8, 1),
        },
      },
    },
    benefitIcon: {
      alignItems: 'center',
      backgroundColor: 'var(--page-builder-accent, #b86f52)',
      borderRadius: '50%',
      color: 'var(--page-builder-accent-contrast, #ffffff)',
      display: 'inline-flex',
      fontSize: '1rem',
      fontWeight: 700,
      height: 38,
      justifyContent: 'center',
      lineHeight: 1,
      width: 38,
      '&.strip': {
        backgroundColor: 'transparent',
        borderRadius: 0,
        color: 'var(--page-builder-text, #211c17)',
        fontSize: '1.8rem',
        height: 42,
        width: 42,
      },
      '&.trust-bar': {
        backgroundColor: 'transparent',
        borderRadius: 0,
        color: 'var(--page-builder-text, #211c17)',
        fontSize: '1.65rem',
        height: 40,
        minWidth: 40,
        width: 'auto',
      },
      '&.review-grid': {
        backgroundColor: 'transparent',
        borderRadius: 0,
        color: 'var(--page-builder-text, #211c17)',
        fontSize: '2.35rem',
        height: 50,
        minWidth: 54,
        width: 'auto',
        '& svg': {
          display: 'block',
          height: '1em',
          width: '1em',
        },
        [theme.breakpoints.down('sm')]: {
          fontSize: '1.95rem',
          height: 42,
          minWidth: 44,
        },
      },
    },
    googleStars: {
      color: '#c99534',
      fontSize: '1.22rem',
      letterSpacing: '.08em',
      lineHeight: 1,
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        fontSize: '.9rem',
      },
    },
    benefitTitle: {
      fontWeight: 700,
      lineHeight: 1.25,
      '&.review-grid': {
        fontSize: '1rem',
      },
      [theme.breakpoints.down('sm')]: {
        '&.review-grid': {
          fontSize: '.82rem',
          lineHeight: 1.25,
        },
      },
    },
    benefitText: {
      display: 'grid',
      gap: theme.spacing(.75),
      minWidth: 0,
      '&.review-grid': {
        justifyItems: 'center',
      },
    },
    benefitBody: {
      color: 'var(--page-builder-muted, #52606d)',
      lineHeight: 1.55,
      '&.review-grid': {
        maxWidth: 220,
        fontSize: '.86rem',
      },
      [theme.breakpoints.down('sm')]: {
        '&.review-grid': {
          fontSize: '.74rem',
          lineHeight: 1.4,
        },
      },
    },
  })
)

interface Props {
  pageType?: 'menu' | 'brand' | 'brandMenu'
  title?: string
  description?: string
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  canonicalUrl?: string
  noindex?: boolean
  brandLogo?: string
  breadcrumb?: BreadcrumbItem[]
  pageBuilder?: MenuPageBuilder
  products?: tProductPreview[]
  productPreviewsByKey?: Record<string, tProductPreview>
  count?: number
  page?: number
  size?: number
  requireStock?: boolean
  subcategoryItems?: NavigationItem[]
  subcategoryParentBrand?: NavigationItem | null
  showCatalogFilters?: boolean
  magazines?: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const normalizeMagazineImageUrl = (value?: string) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('/api/media/landingpages/')) return trimmed.replace('/api/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/media/landingpages/')) return trimmed.replace('/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/images/landingpages/')) return trimmed.replace('/images/landingpages/', '/api/images/landingpages/')
  return trimmed
}

const sanitizeRichTextHtml = (value?: string) =>
  String(value || '')
    .replace(/<p(?:\s[^>]*)?>(?:\s|&nbsp;|\u00a0|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/<div(?:\s[^>]*)?>(?:\s|&nbsp;|\u00a0|<br\s*\/?>)*<\/div>/gi, '')
    .trim()

// The page owns its primary heading; rich-text blocks form sections below it.
const sectionHeadingHtml = (value: string) => value.replace(/<(\/?)(h1)(?=[\s>])/gi, '<$1h2')

const extractFirstHeadingHtml = (value: string) => {
  const match = value.match(/<h([1-3])(?:\s[^>]*)?>([\s\S]*?)<\/h\1>/i)

  if (!match || match.index === undefined) {
    return {
      headingHtml: '',
      bodyHtml: value,
    }
  }

  return {
    headingHtml: match[2].trim(),
    bodyHtml: `${value.slice(0, match.index)}${value.slice(match.index + match[0].length)}`.trim(),
  }
}

type PageBuilderImageRole = 'hero' | 'wide' | 'card' | 'category'

const pageBuilderImageVariants: Record<PageBuilderImageRole, {
  fallbackWidth: number
  sizes: string
  widths: number[]
}> = {
  hero: {
    fallbackWidth: 1600,
    sizes: '100vw',
    widths: [640, 960, 1280, 1600, 1920],
  },
  wide: {
    fallbackWidth: 1200,
    sizes: '(max-width: 900px) 100vw, 1200px',
    widths: [480, 768, 1024, 1200, 1600],
  },
  card: {
    fallbackWidth: 480,
    sizes: '(max-width: 600px) 70vw, (max-width: 1200px) 33vw, 420px',
    widths: [240, 360, 480, 640],
  },
  category: {
    fallbackWidth: 256,
    sizes: '(max-width: 600px) 108px, 128px',
    widths: [128, 256, 384, 640],
  },
}

const getPathnameFromImageSrc = (src: string) => {
  try {
    return new URL(src).pathname
  }
  catch (_err) {
    return src.split(/[?#]/)[0]
  }
}

const getOptimizedLandingpageImageSrc = (src: string, width: number) => {
  const pathname = getPathnameFromImageSrc(src)
  const match = pathname.match(/^\/api\/images\/landingpages\/([^/]+)\/([^/]+)$/)
  if (!match) return ''

  const [, shopId, fileName] = match
  const extensionIndex = fileName.lastIndexOf('.')
  const basename = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName

  return `/image/landingpages/${shopId}/${basename}-w${width}.webp`
}

const getPageBuilderImageProps = (
  src: string,
  role: PageBuilderImageRole = 'wide',
  sizes?: string,
): Pick<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'sizes'> => {
  const imageConfig = { ...pageBuilderImageVariants[role], ...(sizes ? { sizes } : {}) }
  const presentation = config.pageBuilderImagePresentation?.(src)
  if (presentation) return { src, srcSet: presentation.srcSet, sizes: imageConfig.sizes }
  const optimizedSrc = getOptimizedLandingpageImageSrc(src, imageConfig.fallbackWidth)
  if (!optimizedSrc) return getTemplateImageProps(src, imageConfig)

  return {
    src: optimizedSrc,
    srcSet: imageConfig.widths
      .map(width => `${getOptimizedLandingpageImageSrc(src, width)} ${width}w`)
      .join(', '),
    sizes: imageConfig.sizes,
  }
}

const openMagazineReference = (magazine: tProductMagazineReference) => {
  if (typeof window === 'undefined') return

  const targetHash = `#magazin:${magazine.id}:${magazine.pageIndex || 0}`
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
  window.setTimeout(() => {
    window.location.hash = targetHash
  }, 0)
}

const getContainerWidth = (contentWidth: 'contained' | 'wide' | 'full') => {
  switch (contentWidth) {
    case 'full':
      return false
    case 'wide':
      return 'lg' as const
    default:
      return 'lg' as const
  }
}

const getBannerStripTextParts = (value?: string) =>
  String(value || '')
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)

const getItemGapSpacing = (gap?: 'tight' | 'normal' | 'loose') => {
  switch (gap) {
    case 'tight':
      return 8
    case 'loose':
      return 52
    default:
      return 26
  }
}

const getItemShadowClass = (shadow?: 'none' | 'soft' | 'medium') =>
  `shadow-${shadow || 'none'}`

const getCategoryTextSizes = (
  textSize?: 'small' | 'medium' | 'large',
  compact?: boolean
) => {
  if (compact) {
    switch (textSize) {
      case 'small':
        return { title: { xs: '.8rem', md: '.88rem' }, subtitle: { xs: '.75rem', md: '.8rem' } }
      case 'large':
        return { title: { xs: '1rem', md: '1.12rem' }, subtitle: { xs: '.88rem', md: '.95rem' } }
      default:
        return { title: { xs: '.9rem', md: '1rem' }, subtitle: { xs: '.82rem', md: '.88rem' } }
    }
  }

  switch (textSize) {
    case 'small':
      return { title: { xs: '.95rem', md: '1.02rem' }, subtitle: { xs: '.82rem', md: '.92rem' } }
    case 'large':
      return { title: { xs: '1.18rem', md: '1.28rem' }, subtitle: { xs: '.95rem', md: '1rem' } }
    default:
      return { title: { xs: '1.05rem', md: '1.12rem' }, subtitle: { xs: '.88rem', md: '.96rem' } }
  }
}

const getGridTrackMin = (
  shape: 'square' | 'rounded' | 'round' | 'oval' | undefined,
  size: 'small' | 'medium' | 'large' | undefined,
  kind: 'link' | 'brand'
) => {
  const currentShape = shape || (kind === 'brand' ? 'round' : 'rounded')
  const currentSize = size || 'medium'

  if (kind === 'brand') {
    if (currentShape === 'round') {
      if (currentSize === 'small') return 86
      if (currentSize === 'large') return 162
      return 118
    }

    if (currentShape === 'oval') {
      if (currentSize === 'small') return 154
      if (currentSize === 'large') return 258
      return 210
    }

    if (currentShape === 'rounded') {
      if (currentSize === 'small') return 150
      if (currentSize === 'large') return 290
      return 220
    }

    if (currentSize === 'small') return 132
    if (currentSize === 'large') return 240
    return 184
  }

  if (currentShape === 'round') {
    if (currentSize === 'small') return 98
    if (currentSize === 'large') return 194
    return 140
  }

  if (currentShape === 'oval') {
    if (currentSize === 'small') return 170
    if (currentSize === 'large') return 340
    return 288
  }

  if (currentShape === 'rounded') {
    if (currentSize === 'small') return 176
    if (currentSize === 'large') return 360
    return 240
  }

  if (currentSize === 'small') return 164
  if (currentSize === 'large') return 320
  return 220
}

const getSliderWidths = (
  shape: 'square' | 'rounded' | 'round' | 'oval' | undefined,
  size: 'small' | 'medium' | 'large' | undefined,
  kind: 'link' | 'brand'
) => {
  const desktop = getGridTrackMin(shape, size, kind)
  const mobile = kind === 'brand'
    ? Math.max(86, Math.min(desktop, size === 'large' ? 164 : size === 'small' ? 112 : 132))
    : Math.max(112, Math.min(desktop, size === 'large' ? 320 : size === 'small' ? 168 : 240))

  return { desktop, mobile }
}

const getCategorySliderWidths = (
  compact: boolean,
  size?: 'small' | 'medium' | 'large'
) => {
  if (compact) {
    switch (size) {
      case 'small':
        return { desktop: 112, mobile: 96 }
      case 'large':
        return { desktop: 144, mobile: 116 }
      default:
        return { desktop: 126, mobile: 106 }
    }
  }

  switch (size) {
    case 'small':
      return { desktop: 132, mobile: 120 }
    case 'large':
      return { desktop: 236, mobile: 212 }
    default:
      return { desktop: 172, mobile: 152 }
  }
}

const getNativeSliderProps = (
  breakpoints: SwiperOptions['breakpoints'],
  options: { nativeScroll?: boolean } = {}
): SwiperOptions => ({
  navigation: true,
  cssMode: options.nativeScroll === true,
  mousewheel: { forceToAxis: true, releaseOnEdges: true, sensitivity: .75, thresholdDelta: 12 },
  watchOverflow: true,
  speed: 360,
  roundLengths: true,
  slidesPerGroupAuto: true,
  simulateTouch: true,
  grabCursor: true,
  allowTouchMove: true,
  passiveListeners: true,
  touchEventsTarget: 'container',
  touchAngle: 32,
  threshold: 5,
  resistanceRatio: .2,
  touchReleaseOnEdges: true,
  longSwipes: true,
  longSwipesRatio: .18,
  longSwipesMs: 180,
  followFinger: true,
  touchStartPreventDefault: false,
  touchMoveStopPropagation: false,
  preventInteractionOnTransition: false,
  shortSwipes: true,
  touchRatio: 1,
  preventClicks: false,
  preventClicksPropagation: false,
  centeredSlides: false,
  watchSlidesProgress: false,
  breakpoints,
})

const getBannerSliderProps = (): SwiperOptions => ({
  ...getNativeSliderProps({
    600: { slidesPerView: 1, spaceBetween: 0 },
    960: { slidesPerView: 1, spaceBetween: 0 },
  }),
  autoplay: {
    delay: 7000,
    disableOnInteraction: false,
    pauseOnMouseEnter: true,
  },
  effect: 'fade',
  fadeEffect: {
    crossFade: true,
  },
  allowTouchMove: false,
  followFinger: false,
  grabCursor: false,
  longSwipes: false,
  mousewheel: false,
  pagination: {
    clickable: true,
  },
  resistanceRatio: 0,
  rewind: true,
  shortSwipes: false,
  simulateTouch: false,
  speed: 560,
  spaceBetween: 0,
  touchRatio: 0,
})

const getProductsLayoutValue = (block: MenuPageBuilderProductsBlock) =>
  block.layout === 'highlight' || block.layout === 'compact' || block.layout === 'editorial'
    ? block.layout
    : 'grid'

const getProductsDisplayValue = (block: MenuPageBuilderProductsBlock) =>
  block.display === 'slider' || block.display === 'sliderLine' || block.display === 'grid'
    ? block.display
    : block.layout === 'slider' || block.layout === 'sliderLine'
      ? block.layout
      : 'grid'

const resolveBrandImageSrc = (src?: string) => {
  if (!src) return undefined
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src
  return `${config.image_server}/brand/${src}`
}

const LandingPage: React.FC<Props> = ({
  pageType,
  title,
  description,
  metaTitle,
  metaDescription,
  metaKeywords,
  canonicalUrl,
  noindex,
  brandLogo,
  breadcrumb,
  pageBuilder,
  products,
  productPreviewsByKey,
  count,
  page,
  size,
  subcategoryItems,
  subcategoryParentBrand,
  showCatalogFilters,
  magazines,
  landingPageImageAiGenerated,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { filterTree, filterBrands } = useSelector(({ Filter }) => ({
    filterTree: Filter.filterTree,
    filterBrands: Filter.filterBrands,
  }), shallowEqual)
  const hasFilterControls = filterBrands.length > 0
    || filterTree.some(filter => filter.options.length > 0)
  const openFilterDrawer = () => {
    dispatch({ type: 'navigationFilterToggle', payload: true })
    dispatch({ type: 'navigationDrawerToggle', payload: true })
  }
  const router = useRouter()
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated]
  )
  const renderResponsiveAiBadge = (desktop?: string, mobile?: string) => {
    const desktopGenerated = isAiGeneratedLandingImage(desktop)
    const mobileGenerated = isAiGeneratedLandingImage(mobile || desktop)
    if (desktopGenerated === mobileGenerated) return <AiGeneratedBadge aiGenerated={desktopGenerated} placement='top-right' />
    return <>
      <AiGeneratedBadge aiGenerated={desktopGenerated} placement='top-right' className={classes.desktopOnly} />
      <AiGeneratedBadge aiGenerated={mobileGenerated} placement='top-right' className={classes.mobileOnly} />
    </>
  }
  const builder = resolvePageBuilder(pageBuilder)
  const containerWidth = getContainerWidth(builder.layout.contentWidth)
  const stripInHeader =
    builder.layout.navigationPosition === 'top'
    && builder.layout.hideLegacyHeader
  const overlayHeroHeader =
    builder.enabled
    && builder.layout.navigationPosition === 'top'
    && builder.layout.hideLegacyHeader
    && router.pathname === '/'
  const heroImageSrc = builder.hero?.imageSrc || builder.hero?.mobileImageSrc
  const mobileHeroImageProps = builder.hero?.mobileImageSrc
    ? getPageBuilderImageProps(builder.hero.mobileImageSrc, 'hero')
    : undefined
  const heroImageFit = builder.hero?.imageFit || 'cover'
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [contactModalSubject, setContactModalSubject] = useState<MenuPageBuilderContactSubject>('sonstiges')
  const [expandedRichTextBlocks, setExpandedRichTextBlocks] = useState<Record<string, boolean>>({})

  const openContactModal = (subject?: MenuPageBuilderContactSubject) => {
    setContactModalSubject(subject || 'sonstiges')
    setContactModalOpen(true)
  }

  const isContactModalButton = (button: MenuPageBuilderButton) =>
    button.action === 'contactModal'
    || button.href === '#contact-modal'
    || button.href === '#kontaktmodal'

  const renderCtaButton = (
    button: MenuPageBuilderButton,
    variant: 'contained' | 'outlined',
    className: string
  ) => {
    if (isContactModalButton(button)) {
      return <Button
        type='button'
        variant={variant}
        className={className}
        onClick={() => openContactModal(button.contactSubject)}
      >
        {button.label}
      </Button>
    }

    if (!button.href) return null

    if (isAnchorOnlyHref(button.href)) {
      return <Button
        component='a'
        href={button.href}
        variant={variant}
        className={className}
        target={isExternalHref(button.href) ? '_blank' : undefined}
        rel={getExternalLinkRel(button.href, button.externalRel)}
      >
        {button.label}
      </Button>
    }

    return <Link href={button.href} passHref legacyBehavior>
      <Button component='a' variant={variant} className={className}>
        {button.label}
      </Button>
    </Link>
  }

  const getBlockSectionStyle = (
    block: {
      backgroundColor?: string
      paddingTop?: number
      paddingBottom?: number
      divider?: 'none' | 'top' | 'bottom' | 'both'
    },
    extra?: CSSProperties
  ): CSSProperties => {
    const dividerColor = 'rgba(33, 28, 23, .18)'

    return {
      backgroundColor: block.backgroundColor || 'transparent',
      paddingTop: block.paddingTop ?? 56,
      paddingBottom: block.paddingBottom ?? 56,
      borderTop: block.divider === 'top' || block.divider === 'both' ? `1px solid ${dividerColor}` : undefined,
      borderBottom: block.divider === 'bottom' || block.divider === 'both' ? `1px solid ${dividerColor}` : undefined,
      ...extra,
    }
  }

  const renderSectionHeader = (
    block: { title?: string; subtitle?: string; actionLabel?: string; actionHref?: string },
    align: 'left' | 'center' = 'left'
  ) => {
    const hasAction = !!(block.actionLabel && block.actionHref)

    if (!block.title && !block.subtitle && !hasAction) return null

    return <div className={classes.sectionHeaderRow}>
      <div
        className={classes.sectionHeader}
        style={align === 'center' ? { marginInline: 'auto', textAlign: 'center' } : undefined}
      >
        {block.title &&
          <Typography component='h2' variant='h4' align={align === 'center' ? 'center' : undefined}>
            {block.title}
          </Typography>
        }
        {block.subtitle &&
          <Typography
            component='p'
            variant='body1'
            className={classes.subtitle}
            align={align === 'center' ? 'center' : undefined}
          >
            {block.subtitle}
          </Typography>
        }
      </div>

      {hasAction &&
        <PageBuilderLink href={block.actionHref || '#'} className={classes.sectionActionLink}>
          {block.actionLabel}
          <span aria-hidden='true'>→</span>
        </PageBuilderLink>
      }
    </div>
  }

  const renderBanner = (block: MenuPageBuilderBannerBlock, index: number) =>
    (() => {
      const isTopStrip = (block.variant || 'topStrip') === 'topStrip'

      if (isTopStrip && stripInHeader) {
        return null
      }

      if (isTopStrip) {
        const stripTitleParts = getBannerStripTextParts(block.title)
        const stripSubtitleParts = getBannerStripTextParts(block.subtitle)
        const stripBodyParts = getBannerStripTextParts(block.body)
        const renderStripGroup = (
          parts: string[],
          href: string | undefined,
          keyPrefix: string,
          typographySx?: Record<string, unknown>
        ) => {
          if (parts.length === 0) return null

          const content = parts.map((part, partIndex) =>
            <Typography
              key={`${keyPrefix}-${partIndex}`}
              component='span'
              className={classes.bannerStripText}
              sx={typographySx}
            >
              {part}
            </Typography>
          )

          if (!href) return content

          return <PageBuilderLink href={href} className={classes.bannerStripLink}>
            {content}
          </PageBuilderLink>
        }

        return <section
          key={`banner-${index}`}
          style={{ backgroundColor: block.backgroundColor || 'transparent' }}
        >
          <div
            className={classes.bannerStrip}
            style={{
              backgroundColor: block.backgroundColor || 'rgba(255,255,255,0.74)',
            }}
          >
            <Container maxWidth={containerWidth}>
              <div className={classes.bannerStripContent}>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    alignItems: 'center',
                    gap: 1.25,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    {renderStripGroup(
                      stripTitleParts,
                      block.titleHref,
                      `banner-strip-title-${index}`,
                      { fontWeight: 700, display: 'inline-flex' }
                    )}
                  </Box>

                  <Box sx={{ textAlign: 'center' }}>
                    {renderStripGroup(
                      stripSubtitleParts,
                      block.subtitleHref,
                      `banner-strip-subtitle-${index}`,
                      {
                        opacity: .92,
                        textTransform: 'none',
                        letterSpacing: '.02em',
                        fontSize: '.88rem',
                      }
                    )}
                  </Box>

                  <Box sx={{ textAlign: 'right' }}>
                    {renderStripGroup(
                      stripBodyParts,
                      block.bodyHref,
                      `banner-strip-body-${index}`,
                      {
                        opacity: .92,
                        textTransform: 'none',
                        letterSpacing: '.02em',
                        fontSize: '.88rem',
                        display: 'inline-flex',
                        gap: .5,
                      }
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    width: '100%',
                  }}
                >
                  <Typography
                    component='div'
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    {renderStripGroup(
                      stripTitleParts,
                      block.titleHref,
                      `banner-strip-mobile-title-${index}`,
                      {
                        textTransform: 'none',
                        letterSpacing: '.02em',
                        fontSize: '.78rem',
                        lineHeight: 1.3,
                        fontWeight: 700,
                      }
                    )}
                    {renderStripGroup(
                      stripSubtitleParts,
                      block.subtitleHref,
                      `banner-strip-mobile-subtitle-${index}`,
                      {
                        textTransform: 'none',
                        letterSpacing: '.02em',
                        fontSize: '.78rem',
                        lineHeight: 1.3,
                      }
                    )}
                    {renderStripGroup(
                      stripBodyParts,
                      block.bodyHref,
                      `banner-strip-mobile-body-${index}`,
                      {
                        textTransform: 'none',
                        letterSpacing: '.02em',
                        fontSize: '.78rem',
                        lineHeight: 1.3,
                      }
                    )}
                  </Typography>
                </Box>
              </div>
            </Container>
          </div>
        </section>
      }

      if (block.variant === 'story') {
        const storyItems: Array<MenuPageBuilderBannerBlock | MenuPageBuilderBannerItem> =
          block.items?.length ? [block, ...block.items] : [block]
        const renderStoryBannerItem = (
          item: MenuPageBuilderBannerBlock | MenuPageBuilderBannerItem,
          itemIndex: number
        ) => {
          const itemDesktopImageSrc = item.imageSrc || item.mobileImageSrc || block.imageSrc
          const itemMobileImageSrc = item.mobileImageSrc || item.imageSrc || block.mobileImageSrc
          const imageSrc = itemDesktopImageSrc || itemMobileImageSrc
          const isRootItem = itemIndex === 0 && item === block
          const imageTitle = item.imageTitle || (!isRootItem && block.items?.length ? (item.title || '') : '')
          const imageSubtitle = item.imageSubtitle || (!isRootItem && block.items?.length ? (item.subtitle || '') : '')
          const imageBody = item.imageBody || (!isRootItem && block.items?.length ? (item.body || '') : '')
          const imageCta = block.items?.length ? item.cta : undefined
          const imageSecondaryCta = block.items?.length ? item.secondaryCta : undefined
          const hasImageContent = !!(imageTitle || imageSubtitle || imageBody || imageCta || imageSecondaryCta)

          return <div className={classes.storyBanner} key={`story-banner-${index}-${itemIndex}`}>
            <div className={classes.storyIntro}>
              {block.subtitle &&
                <Typography component='span' className={classes.splitBannerEyebrow}>{block.subtitle}</Typography>
              }
              {block.title &&
                <Typography component='h2' variant='h3'>{block.title}</Typography>
              }
              {block.body &&
                <Typography component='p' variant='body2' className={classes.storyBody}>
                  {block.body}
                </Typography>
              }
              {(block.cta || block.secondaryCta) &&
                <div className={classes.splitBannerActions}>
                  {block.cta && renderCtaButton(block.cta, 'contained', classes.splitPrimaryButton)}
                  {block.secondaryCta && renderCtaButton(block.secondaryCta, 'outlined', classes.splitSecondaryButton)}
                </div>
              }
            </div>

            <div className={classes.storyImageWrap}>
              {imageSrc &&
                <ResponsiveImage
                  desktop={getPageBuilderImageProps(imageSrc, 'wide')}
                  mobile={itemMobileImageSrc ? getPageBuilderImageProps(itemMobileImageSrc, 'wide') : undefined}
                  alt={item.imageAlt || item.mobileImageAlt || imageTitle || block.title || ''}
                  className={[classes.storyImage, !itemDesktopImageSrc ? classes.mobileOnly : ''].join(' ')}
                  loading='lazy'
                  decoding='async'
                />
              }
              {renderResponsiveAiBadge(itemDesktopImageSrc, itemMobileImageSrc)}
              {hasImageContent &&
                <>
                  <div className={classes.storyImageShade} />
                  <div className={classes.storyImageContent}>
                    {imageSubtitle &&
                      <Typography component='span' className={classes.eyebrow}>{imageSubtitle}</Typography>
                    }
                    {imageTitle &&
                      <Typography component='h3' variant='h5'>{imageTitle}</Typography>
                    }
                    {imageBody &&
                      <Typography component='p' variant='body2'>{imageBody}</Typography>
                    }
                    {(imageCta || imageSecondaryCta) &&
                      <div className={classes.storyImageActions}>
                        {imageCta && renderCtaButton(imageCta, 'contained', classes.primaryButton)}
                        {imageSecondaryCta && renderCtaButton(imageSecondaryCta, 'outlined', classes.secondaryButton)}
                      </div>
                    }
                  </div>
                </>
              }
            </div>
          </div>
        }

        return <section
          key={`banner-${index}`}
          className={classes.section}
          style={getBlockSectionStyle(block)}
        >
          <Container maxWidth={containerWidth}>
            {storyItems.length > 1
              ? <div
                  className={[classes.bannerSlider, classes.bannerSliderImageControls].join(' ')}
                  style={{ ['--page-builder-banner-mobile-arrow-offset' as string]: '205px' } as CSSProperties}
                >
                  <Swiper
                    slidesPerView={1}
                    spaceBetween={0}
                    {...getBannerSliderProps()}
                  >
                    {storyItems.map((item, itemIndex) =>
                      <SwiperSlide key={`story-banner-${index}-slide-${itemIndex}`}>
                        {renderStoryBannerItem(item, itemIndex)}
                      </SwiperSlide>
                    )}
                  </Swiper>
                </div>
              : renderStoryBannerItem(storyItems[0], 0)
            }
          </Container>
        </section>
      }

      if (block.variant === 'split') {
        const splitItems: Array<MenuPageBuilderBannerBlock | MenuPageBuilderBannerItem> =
          block.items?.length ? [block, ...block.items] : [block]
        const renderSplitBannerItem = (
          item: MenuPageBuilderBannerBlock | MenuPageBuilderBannerItem,
          itemIndex: number
        ) => {
          const itemTitle = item.title || ''
          const itemSubtitle = item.subtitle || ''
          const itemBody = item.body || ''
          const itemCta = item.cta || block.cta
          const itemSecondaryCta = item.secondaryCta || block.secondaryCta
          const itemDesktopImageSrc = item.imageSrc || item.mobileImageSrc || block.imageSrc
          const itemMobileImageSrc = item.mobileImageSrc || item.imageSrc || block.mobileImageSrc
          const imageSrc = itemDesktopImageSrc || itemMobileImageSrc
          const imageTitle = item.imageTitle || ''
          const imageSubtitle = item.imageSubtitle || ''
          const imageBody = item.imageBody || ''
          const hasImageText = !!(imageTitle || imageSubtitle || imageBody)

          return <div className={classes.splitBanner} key={`split-banner-${index}-${itemIndex}`}>
            <div className={classes.splitBannerPanel}>
              {itemSubtitle &&
                <Typography component='span' className={classes.splitBannerEyebrow}>{itemSubtitle}</Typography>
              }
              {itemTitle &&
                <Typography component='h2' variant='h4'>{itemTitle}</Typography>
              }
              {itemBody &&
                <Typography component='p' variant='body2' className={classes.splitBannerBody}>
                  {itemBody}
                </Typography>
              }
              {(itemCta || itemSecondaryCta) &&
                <div className={classes.splitBannerActions}>
                  {itemCta && renderCtaButton(itemCta, 'contained', classes.splitPrimaryButton)}
                  {itemSecondaryCta && renderCtaButton(itemSecondaryCta, 'outlined', classes.splitSecondaryButton)}
                </div>
              }
            </div>
            <div className={classes.splitBannerImageWrap}>
              {imageSrc &&
                <ResponsiveImage
                  desktop={getPageBuilderImageProps(imageSrc, 'wide')}
                  mobile={itemMobileImageSrc ? getPageBuilderImageProps(itemMobileImageSrc, 'wide') : undefined}
                  alt={item.imageAlt || item.mobileImageAlt || itemTitle || block.title || ''}
                  className={[classes.splitBannerImage, !itemDesktopImageSrc ? classes.mobileOnly : ''].join(' ')}
                  loading='lazy'
                  decoding='async'
                />
              }
              {renderResponsiveAiBadge(itemDesktopImageSrc, itemMobileImageSrc)}
              {hasImageText &&
                <>
                  <div className={classes.splitBannerImageShade} />
                  <div className={classes.splitBannerImageContent}>
                    {imageSubtitle &&
                      <Typography component='span' className={classes.eyebrow}>{imageSubtitle}</Typography>
                    }
                    {imageTitle &&
                      <Typography component='h3' variant='h4'>{imageTitle}</Typography>
                    }
                    {imageBody &&
                      <Typography component='p' variant='body2'>{imageBody}</Typography>
                    }
                  </div>
                </>
              }
            </div>
          </div>
        }

        return <section
          key={`banner-${index}`}
          className={classes.section}
          style={getBlockSectionStyle(block)}
        >
          <Container maxWidth={containerWidth}>
            {splitItems.length > 1
              ? <div
                  className={[classes.bannerSlider, classes.bannerSliderImageControls].join(' ')}
                  style={{ ['--page-builder-banner-mobile-arrow-offset' as string]: '140px' } as CSSProperties}
                >
                  <Swiper
                    slidesPerView={1}
                    spaceBetween={0}
                    {...getBannerSliderProps()}
                  >
                    {splitItems.map((item, itemIndex) =>
                      <SwiperSlide key={`split-banner-${index}-slide-${itemIndex}`}>
                        {renderSplitBannerItem(item, itemIndex)}
                      </SwiperSlide>
                    )}
                  </Swiper>
                </div>
              : renderSplitBannerItem(splitItems[0], 0)
            }
          </Container>
        </section>
      }

      const stageItems: Array<MenuPageBuilderBannerBlock | MenuPageBuilderBannerItem> =
        block.items?.length ? [block, ...block.items] : [block]
      const stageMinHeight = block.minHeight === 'small'
        ? 280
        : block.minHeight === 'medium'
          ? 340
          : 420
      const isCompactMobileText = block.mobileLayout === 'compactText'
      const hasMobileSmallText = block.mobileTextSize === 'small'
      const hasMobileTextShadow = block.mobileTextShadow === true

      const renderStageBannerItem = (
        item: MenuPageBuilderBannerBlock | MenuPageBuilderBannerItem,
        itemIndex: number
      ) => {
        const itemDesktopImageSrc = item.imageSrc || item.mobileImageSrc || block.imageSrc
        const itemMobileImageSrc = item.mobileImageSrc || item.imageSrc || block.mobileImageSrc
        const itemImageSrc = itemDesktopImageSrc || itemMobileImageSrc
        const itemTitle = item.title || ''
        const itemSubtitle = item.subtitle || ''
        const itemBody = item.body || ''
        const itemAlign = item.align || block.align || 'left'
        const itemCta = item.cta || block.cta
        const itemSecondaryCta = item.secondaryCta || block.secondaryCta

        return <div
          key={`banner-${index}-item-${itemIndex}`}
          className={[
            classes.banner,
            'fixed-height',
            isCompactMobileText ? 'mobile-compact-text' : '',
          ].join(' ')}
          style={{
            backgroundColor: block.backgroundColor || 'var(--page-builder-surface, #fffaf6)',
            ['--page-builder-banner-height' as string]: `${stageMinHeight}px`,
          }}
        >
          {itemImageSrc &&
            <ResponsiveImage
              desktop={getPageBuilderImageProps(itemImageSrc, 'wide')}
              mobile={itemMobileImageSrc ? getPageBuilderImageProps(itemMobileImageSrc, 'wide') : undefined}
              alt={item.imageAlt || item.mobileImageAlt || itemTitle || block.title || ''}
              className={[classes.bannerImage, isCompactMobileText ? 'mobile-compact-image' : '', !itemDesktopImageSrc ? classes.mobileOnly : ''].join(' ')}
              loading='lazy'
              decoding='async'
            />
          }
          {renderResponsiveAiBadge(itemDesktopImageSrc, itemMobileImageSrc)}
          <div
            className={[classes.bannerOverlay, isCompactMobileText ? classes.bannerCompactOverlay : ''].join(' ')}
            style={{
              ['--banner-overlay-color' as string]: item.overlayColor || block.overlayColor || '#101828',
              ['--banner-overlay-opacity' as string]: (item.overlayOpacity ?? block.overlayOpacity ?? 32) / 100,
              ['--banner-mobile-overlay' as string]: hasMobileTextShadow
                ? 'linear-gradient(90deg, rgba(0,0,0,.68) 0%, rgba(0,0,0,.46) 34%, rgba(0,0,0,.16) 58%, rgba(0,0,0,0) 78%)'
                : 'transparent',
            }}
          />

          <div className={[
            classes.bannerContent,
            `align-${itemAlign}`,
            isCompactMobileText ? 'mobile-compact-text' : '',
            hasMobileSmallText ? 'mobile-text-small' : '',
            hasMobileTextShadow ? 'mobile-text-shadow' : '',
          ].join(' ')}>
            {itemSubtitle &&
              <Typography component='span' className={classes.eyebrow}>{itemSubtitle}</Typography>
            }
            {itemTitle &&
              <Typography component='h2' variant='h3'>{itemTitle}</Typography>
            }
            {itemBody &&
              <Typography component='p' variant='body1'>{itemBody}</Typography>
            }
            {(itemCta || itemSecondaryCta) &&
              <div className={[classes.heroActions, `align-${itemAlign}`].join(' ')}>
                {itemCta && renderCtaButton(itemCta, 'contained', classes.primaryButton)}
                {itemSecondaryCta && renderCtaButton(itemSecondaryCta, 'outlined', classes.secondaryButton)}
              </div>
            }
          </div>
        </div>
      }

      return <section
        key={`banner-${index}`}
        className={classes.section}
        style={getBlockSectionStyle(block)}
      >
        <Container maxWidth={containerWidth}>
          {stageItems.length > 1
            ? <div className={classes.bannerSlider}>
                <Swiper
                  slidesPerView={1}
                  spaceBetween={0}
                  {...getBannerSliderProps()}
                >
                  {stageItems.map((item, itemIndex) =>
                    <SwiperSlide key={`banner-${index}-slide-${itemIndex}`}>
                      {renderStageBannerItem(item, itemIndex)}
                    </SwiperSlide>
                  )}
                </Swiper>
              </div>
            : renderStageBannerItem(stageItems[0], 0)
          }
        </Container>
      </section>
    })()

  const renderLinkGrid = (
    block: MenuPageBuilderCategoryListBlock,
    index: number
  ) => {
    if (block.display === 'editorialSlider' || block.display === 'compactSlider') {
      return renderCategorySlider({
        ...block,
        type: 'categorySlider',
        variant: block.display === 'compactSlider' ? 'compactStrip' : 'editorialCircle',
      } as MenuPageBuilderCategorySliderBlock, index)
    }

    const isSlider = block.display === 'slider'
    const shapeClass = `shape-${block.itemShape || 'rounded'}`
    const sizeClass = `size-${block.itemSize || 'medium'}`
    const shadowClass = getItemShadowClass(block.itemShadow)
    const textSizes = getCategoryTextSizes(block.textSize)
    const gap = getItemGapSpacing(block.itemGap)
    const gridTrackMin = getGridTrackMin(block.itemShape, block.itemSize, 'link')
    const sliderWidths = getSliderWidths(block.itemShape, block.itemSize, 'link')
    const stretchItems = block.stretchItems === true
    const columns = Math.max(1, block.columns || 3)
    const gridStyle = {
      gap,
      ['--link-grid-mobile-columns' as string]: block.itemShape === 'round' ? '2' : '1',
      ['--link-grid-columns' as string]: stretchItems
        ? `repeat(${columns}, minmax(0, 1fr))`
        : `repeat(${columns}, minmax(${gridTrackMin}px, max-content))`,
      ['--link-grid-alignment' as string]: stretchItems ? 'stretch' : 'center',
    }


    return <section
      key={`${block.type}-${index}`}
      className={classes.section}
      style={getBlockSectionStyle(block, {
        ['--page-builder-columns' as string]: String(block.columns || 3),
      } as CSSProperties)}
    >
      <Container maxWidth={containerWidth}>
        <div className={classes.sectionInner}>
          {renderSectionHeader(block)}

          {!isSlider &&
            <div className={classes.linkGrid} style={gridStyle}>
              {block.items.map((item, itemIndex) => <PageBuilderLink
                href={item.href}
                externalRel={item.externalRel}
                key={`${block.type}-${itemIndex}`}
                className={[classes.linkCard, shapeClass, sizeClass, shadowClass].join(' ')}
              >
                  {item.imageSrc &&
                    <img
                      {...getPageBuilderImageProps(item.imageSrc, 'card')}
                      alt={item.imageAlt || item.label}
                      className={classes.linkCardImage}
                      loading='lazy'
                      decoding='async'
                    />
                  }
                  <AiGeneratedBadge aiGenerated={isAiGeneratedLandingImage(item.imageSrc)} size='compact' placement='top-left' />
                  <div className={[classes.linkCardContent, item.imageSrc ? '' : 'plain'].join(' ')}>
                    <Typography component='h3' variant='h5' sx={{ fontSize: textSizes.title, lineHeight: 1.25 }}>
                      {item.label}
                    </Typography>
                    {item.subtitle &&
                      <Typography component='p' variant='body2' sx={{ fontSize: textSizes.subtitle, lineHeight: 1.45 }}>
                        {item.subtitle}
                      </Typography>
                    }
                  </div>
              </PageBuilderLink>)}
            </div>
          }

          {isSlider &&
            <div
              className={classes.linkSliderRoot}
              style={{
                ['--page-builder-link-slide-width' as string]: `${sliderWidths.desktop}px`,
                ['--page-builder-link-slide-width-mobile' as string]: `${sliderWidths.mobile}px`,
              } as CSSProperties}
            >
              <Swiper
                slidesPerView='auto'
                spaceBetween={Math.max(12, gap)}
                {...getNativeSliderProps({
                  600: { spaceBetween: Math.max(14, gap) },
                  960: { spaceBetween: Math.max(16, gap) },
                  1280: { spaceBetween: Math.max(18, gap) },
                }, { nativeScroll: true })}
              >
                {block.items.map((item, itemIndex) => <SwiperSlide key={`${block.type}-slide-${itemIndex}`}>
                  <PageBuilderLink
                    href={item.href}
                    externalRel={item.externalRel}
                    className={[classes.linkCard, shapeClass, sizeClass, shadowClass].join(' ')}
                  >
                      {item.imageSrc &&
                        <img
                          {...getPageBuilderImageProps(item.imageSrc, 'card')}
                          alt={item.imageAlt || item.label}
                          className={classes.linkCardImage}
                          loading='lazy'
                          decoding='async'
                        />
                      }
                      <AiGeneratedBadge aiGenerated={isAiGeneratedLandingImage(item.imageSrc)} size='compact' placement='top-left' />
                      <div className={[classes.linkCardContent, item.imageSrc ? '' : 'plain'].join(' ')}>
                        <Typography component='h3' variant='h5' sx={{ fontSize: textSizes.title, lineHeight: 1.25 }}>
                          {item.label}
                        </Typography>
                        {item.subtitle &&
                          <Typography component='p' variant='body2' sx={{ fontSize: textSizes.subtitle, lineHeight: 1.45 }}>
                            {item.subtitle}
                          </Typography>
                        }
                      </div>
                  </PageBuilderLink>
                </SwiperSlide>)}
              </Swiper>
            </div>
          }
        </div>
      </Container>
    </section>
  }

  const renderBrandList = (
    block: MenuPageBuilderBrandListBlock,
    index: number
  ) => {
    const items = block.items || []
    const isSlider = block.display === 'slider'
    const shapeClass = `shape-${block.itemShape || 'round'}`
    const sizeClass = `size-${block.itemSize || 'medium'}`
    const shadowClass = getItemShadowClass(block.itemShadow || 'none')
    const gap = getItemGapSpacing(block.itemGap)
    const sliderWidths = getSliderWidths(block.itemShape, block.itemSize, 'brand')
    const gridTrackMin = getGridTrackMin(block.itemShape, block.itemSize, 'brand')
    const logoOffsetX = block.logoOffsetX ?? 0
    const logoOffsetY = block.logoOffsetY ?? 0
    const gridStyle = {
      gap,
      ['--brand-grid-columns' as string]: `repeat(${Math.max(1, block.columns || 4)}, minmax(${gridTrackMin}px, max-content))`,
    }


    return <section
      key={`${block.type}-${index}`}
      className={classes.section}
      style={getBlockSectionStyle(block, {
        ['--page-builder-columns' as string]: String(block.columns || 4),
        ['--page-builder-brand-logo-offset-x' as string]: `${logoOffsetX}px`,
        ['--page-builder-brand-logo-offset-y' as string]: `${logoOffsetY}px`,
      } as CSSProperties)}
    >
      <Container maxWidth={containerWidth}>
        <div className={classes.sectionInner}>
          {renderSectionHeader(block)}

          {!isSlider &&
            <div className={classes.brandGrid} style={gridStyle}>
              {items.map((item, itemIndex) => <PageBuilderLink
                href={item.href}
                externalRel={item.externalRel}
                key={`${block.type}-${itemIndex}`}
                className={[classes.brandCard, shapeClass, sizeClass, shadowClass].join(' ')}
              >
                  <div className={classes.brandCardImageWrap}>
                    {resolveBrandImageSrc(item.imageSrc) &&
                      <img
                        src={resolveBrandImageSrc(item.imageSrc)}
                        alt={item.imageAlt || item.label}
                        className={classes.brandCardImage}
                        loading='lazy'
                        decoding='async'
                      />
                    }
                    <AiGeneratedBadge
                      aiGenerated={isAiGeneratedLandingImage(item.imageSrc || resolveBrandImageSrc(item.imageSrc))}
                      size='compact'
                      placement='top-left'
                    />
                  </div>
                  {block.showLabel !== false &&
                    <Typography component='span' className={classes.brandCardLabel}>{item.label}</Typography>
                  }
              </PageBuilderLink>)}
            </div>
          }

          {isSlider &&
            <div
              className={classes.linkSliderRoot}
              style={{
                ['--page-builder-link-slide-width' as string]: `${sliderWidths.desktop}px`,
                ['--page-builder-link-slide-width-mobile' as string]: `${sliderWidths.mobile}px`,
              } as CSSProperties}
            >
              <Swiper
                slidesPerView='auto'
                spaceBetween={Math.max(12, gap)}
                {...getNativeSliderProps({
                  600: { spaceBetween: Math.max(14, gap) },
                  960: { spaceBetween: Math.max(16, gap) },
                  1280: { spaceBetween: Math.max(18, gap) },
                }, { nativeScroll: true })}
              >
                {items.map((item, itemIndex) => <SwiperSlide key={`${block.type}-slide-${itemIndex}`}>
                  <PageBuilderLink
                    href={item.href}
                    externalRel={item.externalRel}
                    className={[classes.brandCard, shapeClass, sizeClass, shadowClass].join(' ')}
                  >
                      <div className={classes.brandCardImageWrap}>
                        {resolveBrandImageSrc(item.imageSrc) &&
                          <img
                            src={resolveBrandImageSrc(item.imageSrc)}
                            alt={item.imageAlt || item.label}
                            className={classes.brandCardImage}
                            loading='lazy'
                            decoding='async'
                          />
                        }
                        <AiGeneratedBadge
                          aiGenerated={isAiGeneratedLandingImage(item.imageSrc || resolveBrandImageSrc(item.imageSrc))}
                          size='compact'
                          placement='top-left'
                        />
                      </div>
                      {block.showLabel !== false &&
                        <Typography component='span' className={classes.brandCardLabel}>{item.label}</Typography>
                      }
                  </PageBuilderLink>
                </SwiperSlide>)}
              </Swiper>
            </div>
          }
        </div>
      </Container>
    </section>
  }

  const renderCategorySlider = (
    block: MenuPageBuilderCategorySliderBlock,
    index: number
  ) => {
    const isCompact = block.variant === 'compactStrip'
    const gap = getItemGapSpacing(block.itemGap)
    const shapeClass = `shape-${block.itemShape || 'round'}`
    const sizeClass = `size-${block.itemSize || 'medium'}`
    const shadowClass = getItemShadowClass(block.itemShadow)
    const textSizes = getCategoryTextSizes(block.textSize, isCompact)
    const stretchSliderItems = block.stretchItems === true
    const showArrows = config.pageBuilderDefaults?.layout?.showCategorySliderArrows === true
    const itemCount = Math.max(1, block.items.length)
    const sliderWidths = getCategorySliderWidths(isCompact, block.itemSize)
    // Match the image shell, which is much smaller than its slider card.
    const imageSize = block.itemSize || 'medium'
    const desktopImageWidth = isCompact
      ? (block.itemShape === 'oval'
        ? { small: 108, medium: 128, large: 148 }[imageSize]
        : { small: 82, medium: 96, large: 112 }[imageSize])
      : { small: 96, medium: 128, large: 178 }[imageSize]
    const mobileImageWidth = isCompact
      ? (block.itemShape === 'oval' ? desktopImageWidth : { small: 76, medium: 86, large: 100 }[imageSize])
      : { small: 84, medium: 108, large: 148 }[imageSize]
    const categoryImageSizes = `(max-width: 600px) ${mobileImageWidth}px, ${desktopImageWidth}px`
    const stretchedBreakpoints = {
      600: { slidesPerView: Math.min(itemCount, isCompact ? 4 : 2), spaceBetween: Math.max(12, gap) },
      960: { slidesPerView: Math.min(itemCount, isCompact ? 6 : 3), spaceBetween: Math.max(14, gap) },
      1280: { slidesPerView: Math.min(itemCount, isCompact ? itemCount : Math.min(itemCount, 4)), spaceBetween: Math.max(16, gap) },
    }
    const initialSlidesPerView = isCompact ? Math.min(itemCount, 2) : 1
    const initialGap = isCompact ? Math.max(10, gap - 2) : Math.max(14, gap)
    // Reserve Swiper's responsive widths before it measures the carousel.
    const initialSlideStyles = stretchSliderItems ? Object.fromEntries([
      ['', { slidesPerView: initialSlidesPerView, spaceBetween: initialGap }],
      ['-sm', stretchedBreakpoints[600]],
      ['-md', stretchedBreakpoints[960]],
      ['-lg', stretchedBreakpoints[1280]],
    ].flatMap(([suffix, dimensions]) => {
      const { slidesPerView, spaceBetween } = dimensions as { slidesPerView: number; spaceBetween: number }
      return [
        [`--category-slide-width${suffix}`, `calc((100% - ${(slidesPerView - 1) * spaceBetween}px) / ${slidesPerView})`],
        [`--category-slide-gap${suffix}`, `${spaceBetween}px`],
      ]
    })) : {}

    const useNativeScroll = nativeHorizontalCarousels && !stretchSliderItems && showArrows
    const CategorySlide = useNativeScroll ? NativeSliderSlide : SwiperSlide
    const slides = block.items.map((item, itemIndex) => <CategorySlide
              key={`${block.type}-${itemIndex}`}
              className={isCompact ? classes.compactSlide : classes.sliderSlide}
            >
              <PageBuilderLink
                href={item.href}
                externalRel={item.externalRel}
                className={isCompact ? classes.compactSliderLink : classes.sliderCardLink}
              >
                  <div className={[
                    isCompact ? classes.compactImageShell : classes.sliderImageShell,
                    shapeClass,
                    sizeClass,
                    shadowClass,
                  ].join(' ')}>
                    {item.imageSrc &&
                      <img
                        {...getPageBuilderImageProps(item.imageSrc, 'category', categoryImageSizes)}
                        alt={item.imageAlt || item.label}
                        className={isCompact ? classes.compactImage : classes.sliderImage}
                        loading='lazy'
                        decoding='async'
                      />
                    }
                    <AiGeneratedBadge aiGenerated={isAiGeneratedLandingImage(item.imageSrc)} size='compact' placement='top-left' />
                  </div>

                  <div className={classes.sliderTextWrap}>
                    <Typography
                      component='h3'
                      className={isCompact ? classes.compactLabel : classes.sliderLabel}
                      sx={{ fontSize: textSizes.title, lineHeight: isCompact ? 1.3 : 1.35 }}
                    >
                      {item.label}
                    </Typography>
                    {!isCompact && item.subtitle &&
                      <Typography
                        component='p'
                        variant='body2'
                        className={classes.sliderSubtitle}
                        sx={{ fontSize: textSizes.subtitle, lineHeight: 1.45 }}
                      >
                        {item.subtitle}
                      </Typography>
                    }
                  </div>
              </PageBuilderLink>
            </CategorySlide>)

    return <section
    key={`${block.type}-${index}`}
    className={classes.section}
    style={getBlockSectionStyle(block, {
      ['--page-builder-category-image-bg' as string]: block.backgroundColor || 'transparent',
      ['--page-builder-slider-slide-width' as string]: `${sliderWidths.desktop}px`,
      ['--page-builder-slider-slide-width-mobile' as string]: `${sliderWidths.mobile}px`,
      ...initialSlideStyles,
    } as CSSProperties)}
  >
    <Container maxWidth={containerWidth}>
      <div className={classes.sectionInner}>
        {renderSectionHeader(block)}

        <div className={[classes.sliderRoot, isCompact ? 'compact' : '', stretchSliderItems ? 'stretch-items' : ''].join(' ')}>
          {useNativeScroll ? <NativeHorizontalSlider
            label={block.title || 'Kategorien'}
            spaceBetween={initialGap}
            breakpoints={{
              600: { spaceBetween: isCompact ? Math.max(12, gap) : Math.max(18, gap) },
              960: { spaceBetween: isCompact ? Math.max(14, gap) : Math.max(22, gap) },
              1280: { spaceBetween: isCompact ? Math.max(16, gap) : Math.max(24, gap) },
            }}
          >{slides}</NativeHorizontalSlider> : <Swiper
            slidesPerView={stretchSliderItems
              ? initialSlidesPerView
              : 'auto'}
            spaceBetween={initialGap}
            {...getNativeSliderProps(stretchSliderItems
              ? stretchedBreakpoints
              : {
                  600: { spaceBetween: isCompact ? Math.max(12, gap) : Math.max(18, gap) },
                  960: { spaceBetween: isCompact ? Math.max(14, gap) : Math.max(22, gap) },
                  1280: { spaceBetween: isCompact ? Math.max(16, gap) : Math.max(24, gap) },
                }, { nativeScroll: !stretchSliderItems })}
            navigation={!showArrows}
            arrowLabel={showArrows ? block.title || 'Kategorien' : undefined}
          >
            {slides}
          </Swiper>}
        </div>
      </div>
    </Container>
  </section>
  }

  const renderRichText = (block: MenuPageBuilderRichTextBlock, index: number) => {
    const blockKey = block.id || `richtext-${index}`
    const isExpandable = block.readMoreEnabled === true
    const isExpanded = expandedRichTextBlocks[blockKey] === true
    const isCollapsed = isExpandable && !isExpanded
    const align = block.align || 'left'
    const lineCount = Math.max(1, Math.min(block.readMoreLines || 6, 40))

    return <section
      key={`richtext-${index}`}
      className={classes.section}
      style={getBlockSectionStyle(block)}
    >
      <Container maxWidth={containerWidth}>
        <div className={classes.sectionInner}>
          {renderSectionHeader(block, align)}

          <div
            className={[
              classes.richText,
              align === 'center' ? 'center' : '',
              block.maxWidth === 'narrow' ? 'narrow' : '',
              isCollapsed ? 'collapsed' : '',
            ].join(' ')}
            style={{
              ['--rich-text-collapsed-height' as string]: `${lineCount * 1.8}em`,
              ['--rich-text-fade-bg' as string]: block.backgroundColor || builder.theme.backgroundColor,
            } as CSSProperties}
          >
            {parse(sectionHeadingHtml(sanitizeRichTextHtml(block.bodyHtml)))}
          </div>

          {isExpandable &&
            <Button
              className={[
                classes.richTextToggle,
                align === 'center' ? 'center' : '',
              ].join(' ')}
              variant='outlined'
              onClick={() => setExpandedRichTextBlocks(current => ({
                ...current,
                [blockKey]: !isExpanded,
              }))}
            >
              {isExpanded
                ? block.readLessLabel || 'Weniger anzeigen'
                : block.readMoreLabel || 'Mehr lesen'
              }
            </Button>
          }
        </div>
      </Container>
    </section>
  }

  const renderLegacyDescriptionIntro = (sectionOnly = false) => {
    const cleanDescription = sanitizeRichTextHtml(description)
    if (!cleanDescription) return null
    const descriptionParts = sectionOnly || !builder.hero
      ? extractFirstHeadingHtml(cleanDescription)
      : { headingHtml: '', bodyHtml: cleanDescription }
    const introHeadingHtml = descriptionParts.headingHtml
    const bodyDescription = sectionHeadingHtml(descriptionParts.bodyHtml)

    const blockKey = 'legacy-description'
    const isExpanded = expandedRichTextBlocks[blockKey] === true
    const isCollapsed = !isExpanded

    return <section className={classes.legacyIntro}>
      <Container maxWidth={containerWidth}>
        <div className={classes.legacyIntroInner}>
          {!sectionOnly && !builder.hero && brandLogoSrc &&
            <span className={classes.heroBrandLogoWrap}>
              <img
                src={brandLogoSrc}
                alt={title ? `Logo von ${title}` : 'Herstellerlogo'}
                className={classes.legacyIntroBrandLogo}
                decoding='async'
              />
              <AiGeneratedBadge aiGenerated={isAiGeneratedLandingImage(brandLogoSrc)} size='compact' placement='top-left' />
            </span>
          }

          {introHeadingHtml &&
            <Typography
              component={sectionOnly ? 'h2' : 'h1'}
              className={[
                classes.legacyIntroTitle,
                classes.legacyIntroSeoTitle,
              ].join(' ')}
            >
              {descriptionParts.headingHtml ? parse(descriptionParts.headingHtml) : introHeadingHtml}
            </Typography>
          }

          {!sectionOnly && !builder.hero && !introHeadingHtml &&
            <Typography component='h1' className={classes.legacyIntroTitle}>
              {primaryHeading}
            </Typography>
          }

          <div
            className={[
              classes.richText,
              'wide',
              isCollapsed ? 'collapsed' : '',
            ].join(' ')}
            style={{
              ['--rich-text-collapsed-height' as string]: '9em',
              ['--rich-text-fade-bg' as string]: builder.theme.backgroundColor,
            } as CSSProperties}
          >
            {parse(bodyDescription)}
          </div>

          <Button
            variant='text'
            className={classes.legacyIntroToggle}
            onClick={() => setExpandedRichTextBlocks(current => ({
              ...current,
              [blockKey]: !isExpanded,
            }))}
          >
            {isExpanded ? 'Weniger anzeigen' : 'Weiter lesen'}
          </Button>

          <hr className={classes.legacyIntroDivider} />
        </div>
      </Container>
    </section>
  }

  const renderLineIcon = (icon: 'consultation' | 'materials' | 'avoidMistakes' | 'experience' | 'freeConsultation') => {
    switch (icon) {
      case 'consultation':
        return <svg viewBox='0 0 48 48' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <path d='M18 30c-5.6 1.3-9 4.2-9 8v2h18v-2c0-3.8-3.4-6.7-9-8Z' />
          <circle cx='18' cy='19' r='5.5' />
          <path d='M31 27c4.7 1 8 3.6 8 7v1.8h-8' />
          <path d='M29 14.5a5 5 0 0 1 0 9.5' />
          <path d='M28 10h10c2.2 0 4 1.6 4 3.7v6.6c0 2.1-1.8 3.7-4 3.7h-3.5L29 28v-4h-1' />
        </svg>
      case 'materials':
        return <svg viewBox='0 0 48 48' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <path d='M9 30h9.5c2.6 0 4.7 2.1 4.7 4.7H15' />
          <path d='M23 34.7h8.5c2.2 0 4.4-.8 6.1-2.2l3.8-3.1a3 3 0 0 0-3.9-4.6l-5.6 3.9' />
          <path d='M9 25.5h7.5c3.6 0 5.8 1.2 8.5 3.2h6.2a3 3 0 1 1 0 6' />
          <rect x='24' y='9' width='17' height='11' rx='2' />
          <path d='M27 14.5h8' />
        </svg>
      case 'avoidMistakes':
        return <svg viewBox='0 0 48 48' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <path d='M8 23 24 10l16 13' />
          <path d='M13 22v17h22V22' />
          <path d='M20 39V27h8v12' />
          <circle cx='34' cy='34' r='7' />
          <path d='m30.8 34.2 2.2 2.2 4.4-5' />
        </svg>
      case 'experience':
        return <svg viewBox='0 0 48 48' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <path d='M24 7 38 12v10c0 9.6-5.4 16.1-14 19-8.6-2.9-14-9.4-14-19V12l14-5Z' />
          <path d='m18.5 24.5 4 4 8-9' />
        </svg>
      case 'freeConsultation':
        return <svg viewBox='0 0 48 48' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <path d='M24 39S9 30.5 9 18.5A8.1 8.1 0 0 1 24 14a8.1 8.1 0 0 1 15 4.5C39 30.5 24 39 24 39Z' />
        </svg>
    }
  }

  const getBenefitIcon = (icon?: string) => {
    switch (icon) {
      case 'support':
        return <ForumOutlinedIcon fontSize='inherit' />
      case 'consultation':
        return renderLineIcon('consultation')
      case 'person':
        return <PersonOutlineIcon fontSize='inherit' />
      case 'store':
        return <StorefrontOutlinedIcon fontSize='inherit' />
      case 'location':
        return <LocationOnOutlinedIcon fontSize='inherit' />
      case 'schedule':
        return <ScheduleOutlinedIcon fontSize='inherit' />
      case 'phone':
        return <LocalPhoneOutlinedIcon fontSize='inherit' />
      case 'mail':
        return <EmailOutlinedIcon fontSize='inherit' />
      case 'truck':
        return <LocalShippingOutlinedIcon fontSize='inherit' />
      case 'star':
        return <StarBorderIcon fontSize='inherit' />
      case 'premium':
        return <WorkspacePremiumOutlinedIcon fontSize='inherit' />
      case 'shield':
        return <GppGoodOutlinedIcon fontSize='inherit' />
      case 'security':
        return <VerifiedUserOutlinedIcon fontSize='inherit' />
      case 'heart':
        return <FavoriteBorderOutlinedIcon fontSize='inherit' />
      case 'document':
        return <DescriptionOutlinedIcon fontSize='inherit' />
      case 'handshake':
        return <HandshakeOutlinedIcon fontSize='inherit' />
      case 'materials':
        return renderLineIcon('materials')
      case 'avoidMistakes':
        return renderLineIcon('avoidMistakes')
      case 'experience':
        return renderLineIcon('experience')
      case 'freeConsultation':
        return renderLineIcon('freeConsultation')
      case 'googleStars':
        return <span className={classes.googleStars}>★★★★★</span>
      case 'googleReview':
        return <StarBorderIcon fontSize='inherit' />
      case 'assembly':
        return <BuildOutlinedIcon fontSize='inherit' />
      case 'payment':
        return <CreditCardOutlinedIcon fontSize='inherit' />
      case 'return':
        return <Replay30OutlinedIcon fontSize='inherit' />
      default:
        return <CheckCircleOutlineIcon fontSize='inherit' />
    }
  }

  const renderBenefits = (block: MenuPageBuilderBenefitsBlock, index: number) => {
    const isStrip = block.variant === 'strip'
    const isTrustBar = block.variant === 'trustBar'
    const isReviewGrid = block.variant === 'reviewGrid'
    const benefitVariantClass = isReviewGrid ? 'review-grid' : isTrustBar ? 'trust-bar' : isStrip ? 'strip' : ''
    const googleRating = block.googleRating || shopReviewInfos.googleRating
    const googleReviewCount = block.googleReviewCount || shopReviewInfos.googleReviewCount
    const googleReviewHref = block.googleReviewHref || shopReviewInfos.googleReviewHref
    const getDisplayItem = (item: MenuPageBuilderBenefitsBlock['items'][number]) => {
      const isGoogleItem = item.icon === 'googleStars' || item.icon === 'googleReview'

      if (block.reviewSource !== 'google' || !isGoogleItem || !googleRating) return item

      return {
        ...item,
        title: `${googleRating} Sterne${googleReviewCount ? ` bei ${googleReviewCount} Kunden` : ''}`,
        body: item.body || 'aus Google Bewertungen',
      }
    }

    return <section
      key={`benefits-${index}`}
      className={classes.section}
      style={getBlockSectionStyle(block, {
        ['--page-builder-benefit-columns' as string]: String(block.columns || 3),
      } as CSSProperties)}
    >
      <Container maxWidth={containerWidth}>
        <div className={classes.sectionInner}>
          {renderSectionHeader(block)}

          <div className={[classes.benefitsGrid, benefitVariantClass].join(' ')}>
            {block.items.map((item, itemIndex) => {
              const displayItem = getDisplayItem(item)
              const googleItem = displayItem.icon === 'googleStars' || displayItem.icon === 'googleReview'
              const cardProps = googleItem && block.reviewSource === 'google' && googleReviewHref
                ? {
                    component: 'a' as const,
                    href: googleReviewHref,
                    target: '_blank',
                    rel: getExternalLinkRel(googleReviewHref),
                  }
                : {}

              return <Box key={`${block.id || 'benefit'}-${itemIndex}`} className={[classes.benefitCard, benefitVariantClass].join(' ')} {...cardProps}>
                <span className={[classes.benefitIcon, benefitVariantClass].join(' ')}>{getBenefitIcon(displayItem.icon)}</span>
                <div className={[classes.benefitText, benefitVariantClass].join(' ')}>
                  {displayItem.title &&
                    <Typography component='h3' variant='h6' className={[classes.benefitTitle, benefitVariantClass].join(' ')}>
                      {displayItem.title}
                    </Typography>
                  }
                  {displayItem.body &&
                    <Typography component='p' variant='body2' className={[classes.benefitBody, benefitVariantClass].join(' ')}>
                      {displayItem.body}
                    </Typography>
                  }
                </div>
              </Box>
            })}
          </div>
        </div>
      </Container>
    </section>
  }

  const renderProducts = (block: MenuPageBuilderProductsBlock, index: number) => {
    const productKeys = block.productKeys || []
    const {
      keys,
      products: suppliedProducts,
      useFilteredProducts,
    } = resolveProductBlockSelection(block, products, productPreviewsByKey)
    const filteredProducts = useFilteredProducts ? products || [] : undefined
    const productLayout = getProductsLayoutValue(block)
    const productDisplay = getProductsDisplayValue(block)
    const showCatalogLayout = useFilteredProducts && showCatalogFilters === true && (hasFilterControls || typeof count === 'number')
    const showFilterRegion = showCatalogLayout
    const currentPage = page || 1
    const currentSize = size || filteredProducts?.length || 12
    const currentCount = count ?? filteredProducts?.length ?? 0
    const columnsPerRowMobile = showCatalogLayout ? 2 : builder.products.columnsPerRowMobile

    const productRow = <ProductRow
      key={`${index}-${productLayout}-${productDisplay}-${block.selectionMode || ''}-${productKeys.join('|')}-${suppliedProducts ? suppliedProducts.map(product => product.key).join('|') : ''}`}
      display={[
        'title',
        ...(block.showPrice === false ? [] : ['price']),
        ...(block.showAttributes === false ? [] : ['attributes']),
        ...(block.showHookText === true ? ['hook'] : []),
        ...(block.showAvailability === false ? [] : ['deliverytime']),
        ...(block.cardBackgroundColor
          ? [`bg-card-${block.cardBackgroundColor}`]
          : []),
      ] as any}
      size={suppliedProducts ? suppliedProducts.length : keys?.length || 12}
      keys={keys}
      products={suppliedProducts}
      inStockOnly={block.inStockOnly === true}
      layout={productLayout}
      displayMode={productDisplay}
      showDiscount={block.showSpecialPrice !== false}
      showAvailability={block.showAvailability !== false}
      showHoverEffect={block.showHoverEffect !== false}
      preferMoodImages={block.preferMoodImages === true}
      showHookText={block.showHookText === true}
      showWishlist={block.showWishlist !== false}
      imageSizePx={block.imageSizePx || 250}
      columnsPerRow={builder.products.columnsPerRow}
      columnsPerRowMobile={columnsPerRowMobile}
      sliderLabel={block.title || 'Produkte'}
    />

    return <section
      key={`products-${index}`}
      className={classes.section}
      style={getBlockSectionStyle(block)}
    >
      <Container maxWidth={containerWidth}>
        <div className={classes.sectionInner}>
          {renderSectionHeader(block)}
          {showCatalogLayout
            ? <div className={[
                classes.catalogLayout,
                showFilterRegion ? classes.catalogLayoutWithSidebar : '',
              ].join(' ')}>
                {showFilterRegion &&
                  <aside className={classes.catalogSidebar} aria-label='Produktfilter'>
                    <div className={classes.catalogSidebarInner}>
                      <Typography component='h2' className={classes.filterSidebarTitle}>
                        Filtern
                      </Typography>
                      {hasFilterControls &&
                        <FilterTreeAccordion />
                      }
                    </div>
                  </aside>
                }
                <div className={classes.catalogProducts}>
                  {productRow}
                  {currentCount > currentSize &&
                    <ProductsPagination page={currentPage} size={currentSize} count={currentCount} />
                  }
                </div>
              </div>
            : productRow
          }
        </div>
      </Container>
    </section>
  }

  const renderBlock = (block: MenuPageBuilderBlock, index: number) => {
    switch (block.type) {
      case 'banner':
        return renderBanner(block, index)
      case 'categoryList':
        return renderLinkGrid(block, index)
      case 'brandList':
        return renderBrandList(block, index)
      case 'categorySlider':
        return renderCategorySlider(block, index)
      case 'richText':
        return renderRichText(block, index)
      case 'benefits':
        return renderBenefits(block, index)
      case 'products':
        return renderProducts(block, index)
      default:
        return null
    }
  }

  const hasProductsBlock = builder.blocks.some(block => block.type === 'products')
  const renderFilteredProductsFallback = products !== undefined && products.length > 0 && !hasProductsBlock
  const hasCatalogTopBar = showCatalogFilters === true
    && hasPageProductSelection(builder.blocks)
    && products !== undefined
    && typeof count === 'number'
  const hasSubcategoryTopBar = Boolean(subcategoryItems?.length)
  const filteredProductsFallbackBlock: MenuPageBuilderProductsBlock = {
    type: 'products',
    productKeys: [],
    paddingTop: hasCatalogTopBar
      ? (hasSubcategoryTopBar ? 40 : 12)
      : builder.blocks.length > 0 || builder.hero || description
        ? 40
        : 56,
    paddingBottom: 56,
    divider: 'top',
    preferMoodImages: false,
  }

  const primaryHeading = seoText(builder.hero?.title) || seoText(title) || seoText(metaTitle) || shopInfos.title || shopInfos.name
  const validTitle = seoText(metaTitle) || seoText(title) || primaryHeading
  const validDescription = seoDescription(seoText(metaDescription) || seoText(description) || seoText(builder.hero?.body), shopInfos.description)
  const managedDescriptionText = seoText(description)
  const managedDescriptionBody = seoText(extractFirstHeadingHtml(sanitizeRichTextHtml(description)).bodyHtml) || managedDescriptionText
  const hasManagedDescriptionBlock = builder.blocks.some(block => block.type === 'richText'
    && (seoText(block.bodyHtml).includes(managedDescriptionText)
      || seoText(block.bodyHtml).includes(managedDescriptionBody)))
  const showManagedDescription = Boolean(pageType && (page || 1) === 1 && builder.blocks.length > 0
    && managedDescriptionText && !hasManagedDescriptionBlock)
  // Curated blocks can show different products from the current category result.
  const structuredProducts = hasProductsBlock
    ? builder.blocks.flatMap(block => {
        if (block.type !== 'products') return []
        const selected = resolveProductBlockSelection(block, products, productPreviewsByKey).products || []
        return block.inStockOnly ? selected.filter(product => typeof product.stock === 'number' && product.stock > 0) : selected
      }).filter((product, index, all) => all.findIndex(item => item.key === product.key) === index)
    : products
  const heroLayout = builder.hero?.layout || 'background'
  const splitHeroImageSrc = builder.hero?.imageSrc || builder.hero?.mobileImageSrc
  const splitHeroPresentation = splitHeroImageSrc
    ? config.pageBuilderImagePresentation?.(splitHeroImageSrc)
    : undefined
  const containedBrandHero = heroImageFit === 'contain' && Boolean(splitHeroPresentation)
  // Wide editorial rasters retain their complete composition at every viewport.
  const splitHeroNaturalSize = splitHeroPresentation?.width && splitHeroPresentation?.height
    && splitHeroPresentation.width > 0 && splitHeroPresentation.height > 0
    ? { width: splitHeroPresentation.width, height: splitHeroPresentation.height }
    : undefined
  const brandLogoSrc = resolveBrandImageSrc(brandLogo)
  const splitHeroHasLogoOverlay = hasBrandLogoOverlay(splitHeroPresentation, brandLogoSrc)
  const heroTitleClass = brandLogoSrc && !splitHeroPresentation?.logoEmbedded && !splitHeroHasLogoOverlay ? classes.heroBrandTextTitle : classes.heroSplitTitle
  const backgroundHeroTitleClass = brandLogoSrc ? classes.heroBrandTextTitle : classes.heroTitle
  const splitHeroHeight = builder.hero?.height === 'screen'
    ? 440
    : builder.hero?.height === 'medium'
      ? 300
      : 360
  const filterTopBar = hasCatalogTopBar
    ? <Container maxWidth={containerWidth}>
        <FilterTopBar
          subcategoryItems={subcategoryItems}
          subcategoryParentBrand={subcategoryParentBrand}
          count={count}
          showFilterButton
          onFilterClick={openFilterDrawer}
        />
      </Container>
    : null
  const magazinePlacement = builder.layout.magazinePlacement || 'afterFilters'
  const breadcrumbTrail = breadcrumb?.length
    ? <Container maxWidth={containerWidth} className={classes.breadcrumbWrap}>
        <BreadcrumbTrail items={breadcrumb} />
      </Container>
    : null
  const magazineFeature = magazines && magazines.length > 0 && magazinePlacement !== 'hidden'
    ? <Container maxWidth={containerWidth}>
      <section className={classes.magazineFeature}>
        <div className={classes.magazineFeatureHeader}>
          <span>Aus dem Magazin</span>
          <h2>Stories, Ideen und Berichte</h2>
        </div>
        <div className={classes.magazineFeatureGrid}>
          {magazines.map(magazine => {
            const href = magazine.href || magazineUrl(magazine.id, magazine.slug, magazine.pageIndex)

            return <a
              key={magazine.id}
              className={classes.magazineFeatureCard}
              href={href}
              onClick={event => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
                event.preventDefault()
                openMagazineReference(magazine)
              }}
            >
              <span className={classes.magazineFeatureCover}>
                {magazine.coverImage?.src
                  ? <img
                      {...getPageBuilderImageProps(normalizeMagazineImageUrl(magazine.coverImage.src), 'card')}
                      alt={magazine.coverImage.alt || magazine.title}
                      loading='lazy'
                      decoding='async'
                    />
                  : <span>{magazine.title}</span>
                }
                <AiGeneratedBadge
                  aiGenerated={magazine.coverImage?.aiGenerated || isAiGeneratedLandingImage(magazine.coverImage?.src)}
                  size='compact'
                  placement='top-left'
                />
              </span>
              <span className={classes.magazineFeatureText}>
                {magazine.issue && <small>{magazine.issue}</small>}
                <strong>{magazine.title}</strong>
                {magazine.subtitle && <span>{magazine.subtitle}</span>}
              </span>
            </a>
          })}
        </div>
      </section>
    </Container>
    : null
  const renderMagazineFeature = (placement: typeof magazinePlacement) =>
    magazinePlacement === placement ? magazineFeature : null

  return <div className={classes.page}>
    <Seo
      title={validTitle}
      description={validDescription}
      keywords={metaKeywords}
      canonical={canonicalUrl || '/'}
      image={splitHeroImageSrc || brandLogoSrc}
      imageAlt={builder.hero?.imageAlt || builder.hero?.mobileImageAlt || primaryHeading}
      robots={noindex ? 'noindex,follow' : undefined}
    />
    <SeoBreadcrumbJsonLd items={breadcrumb} />
    <FilterStructuredData
      pageType={pageType}
      title={title}
      description={description}
      metaTitle={metaTitle}
      metaDescription={metaDescription}
      canonicalUrl={canonicalUrl}
      brandLogoSrc={brandLogoSrc}
      products={structuredProducts}
      magazines={magazinePlacement === 'hidden' ? undefined : magazines}
      page={page}
      size={size}
    />

    {!builder.hero && breadcrumbTrail}

    {!builder.hero && (builder.blocks.length > 0 || !sanitizeRichTextHtml(description)) &&
      <Container maxWidth={containerWidth}>
        <Typography component='h1' className={classes.legacyIntroTitle} sx={{ pt: 4, pb: 2 }}>
          {primaryHeading}
        </Typography>
      </Container>
    }

    {builder.hero && heroLayout === 'split' &&
      <section
        className={classes.heroSplitSection}
        style={{
          ['--page-builder-hero-split-height' as string]: `${splitHeroHeight}px`,
        } as CSSProperties}
      >
        <Container maxWidth={containerWidth}>
          <div className={[
            classes.heroSplit,
            !splitHeroImageSrc && typeof config.pageBuilderImagePresentation === 'function' ? classes.heroSplitWithoutImage : '',
          ].join(' ')}>
            <div className={[classes.heroSplitText, `align-${builder.hero.align || 'left'}`].join(' ')}>
              <BreadcrumbTrail items={breadcrumb} />

              {brandLogoSrc && !splitHeroPresentation?.logoEmbedded && !splitHeroHasLogoOverlay &&
                <span className={classes.heroBrandLogoWrap}>
                  <img
                    src={brandLogoSrc}
                    alt={title ? `Logo von ${title}` : (builder.hero.title || 'Herstellerlogo')}
                    className={classes.heroBrandLogo}
                    decoding='async'
                  />
                  <AiGeneratedBadge aiGenerated={isAiGeneratedLandingImage(brandLogoSrc)} size='compact' placement='top-left' />
                </span>
              }

              {builder.hero.eyebrow &&
                <Typography component='span' className={classes.eyebrow}>{builder.hero.eyebrow}</Typography>
              }
              {primaryHeading &&
                <Typography
                  component='h1'
                  className={heroTitleClass}
                >
                  {primaryHeading}
                </Typography>
              }
              {builder.hero.body &&
                <Typography component='p' className={classes.heroSplitBody}>
                  {builder.hero.body}
                </Typography>
              }

              {(builder.hero.cta || builder.hero.secondaryCta) &&
                <div className={[classes.heroActions, `align-${builder.hero.align || 'left'}`].join(' ')}>
                  {builder.hero.cta && renderCtaButton(builder.hero.cta, 'contained', classes.primaryButton)}
                  {builder.hero.secondaryCta && renderCtaButton(builder.hero.secondaryCta, 'outlined', classes.heroSplitSecondaryButton)}
                </div>
              }
            </div>

            {splitHeroImageSrc &&
              <div
                className={[
                  classes.heroSplitImageWrap,
                  splitHeroNaturalSize ? classes.heroSplitNaturalImageWrap : '',
                ].join(' ')}
                data-brand-hero={splitHeroPresentation ? 'true' : undefined}
                data-brand-id={splitHeroPresentation?.brandId}
                data-product-id={splitHeroPresentation?.productId}
                style={heroImageFit === 'contain'
                  ? {
                      backgroundColor: builder.theme.surfaceColor || '#ffffff',
                      ...(containedBrandHero ? { height: 'clamp(260px, 40vw, 440px)' } : {}),
                    }
                  : undefined
                }
              >
                <ResponsiveImage
                  desktop={{ ...getPageBuilderImageProps(splitHeroImageSrc, 'hero'), sizes: '(max-width: 899.95px) 100vw, 50vw' }}
                  mobile={mobileHeroImageProps}
                  alt={builder.hero.imageAlt || builder.hero.mobileImageAlt || builder.hero.title || ''}
                  className={splitHeroNaturalSize && !containedBrandHero ? classes.heroSplitNaturalImage : classes.heroSplitImage}
                  {...splitHeroNaturalSize}
                  style={{ objectFit: heroImageFit }}
                  loading='eager'
                  {...{ fetchpriority: 'high' }}
                  decoding='async'
                />
                <BrandLogoOverlay
                  presentation={splitHeroPresentation}
                  fallbackLogoSrc={brandLogoSrc}
                  fallbackAlt={title ? `Logo von ${title}` : 'Herstellerlogo'}
                />
                <AiGeneratedBadge aiGenerated={isAiGeneratedLandingImage(splitHeroImageSrc)} placement='top-right' />
              </div>
            }
          </div>
        </Container>
      </section>
    }

    {builder.hero && heroLayout !== 'split' &&
      <section
        className={[classes.hero, `align-${builder.hero.align || 'left'}`].join(' ')}
        style={{
          ['--hero-mobile-height' as string]: builder.layout.heroMobileMode === 'fullscreen'
            ? (overlayHeroHeader ? '100svh' : 'calc(100svh - 64px)')
            : '420px',
          ['--hero-desktop-height' as string]: builder.hero.height === 'screen'
            ? (overlayHeroHeader ? '100vh' : 'calc(100vh - 64px)')
            : builder.hero.height === 'medium' ? '480px' : '620px',
        }}
      >
        {heroImageSrc &&
          <ResponsiveImage
            desktop={getPageBuilderImageProps(heroImageSrc, 'hero')}
            mobile={mobileHeroImageProps}
            alt={builder.hero.imageAlt || builder.hero.mobileImageAlt || builder.hero.title || ''}
            className={classes.heroImage}
            style={{ objectFit: heroImageFit }}
            loading='eager'
            {...{ fetchpriority: 'high' }}
            decoding='async'
          />
        }
        <AiGeneratedBadge aiGenerated={isAiGeneratedLandingImage(heroImageSrc)} placement='top-right' />
        <div
          className={classes.heroOverlay}
          style={{
            backgroundColor: builder.hero.overlayColor || '#101828',
            opacity: (builder.hero.overlayOpacity ?? 38) / 100,
          }}
        />

        <Container
          maxWidth={containerWidth}
          className={classes.heroContent}
          style={overlayHeroHeader ? { paddingTop: 112 } : undefined}
        >
          <div className={[classes.heroInner, `align-${builder.hero.align || 'left'}`].join(' ')}>
            {brandLogoSrc &&
              <span className={classes.heroBrandLogoWrap}>
                <img
                  src={brandLogoSrc}
                  alt={title ? `Logo von ${title}` : (builder.hero.title || 'Herstellerlogo')}
                  className={classes.heroBrandLogo}
                  decoding='async'
                />
                <AiGeneratedBadge aiGenerated={isAiGeneratedLandingImage(brandLogoSrc)} size='compact' placement='top-left' />
              </span>
            }

            {builder.hero.eyebrow &&
              <Typography component='span' className={classes.eyebrow}>{builder.hero.eyebrow}</Typography>
            }
            {primaryHeading &&
              <Typography
                component='h1'
                className={backgroundHeroTitleClass}
              >
                {primaryHeading}
              </Typography>
            }
            {builder.hero.body &&
              <Typography
                component='p'
                className={[classes.heroBody, `align-${builder.hero.align || 'left'}`].join(' ')}
              >
                {builder.hero.body}
              </Typography>
            }

            {(builder.hero.cta || builder.hero.secondaryCta) &&
              <div className={[classes.heroActions, `align-${builder.hero.align || 'left'}`].join(' ')}>
                {builder.hero.cta && renderCtaButton(builder.hero.cta, 'contained', classes.primaryButton)}
                {builder.hero.secondaryCta && renderCtaButton(builder.hero.secondaryCta, 'outlined', classes.secondaryButton)}
              </div>
            }
          </div>
        </Container>
      </section>
    }

    {builder.hero && heroLayout !== 'split' && breadcrumbTrail}

    {renderMagazineFeature('afterHero')}

    {builder.blocks.length === 0 && renderLegacyDescriptionIntro()}

    {filterTopBar}

    {renderMagazineFeature('afterFilters')}

    {builder.blocks.length > 0 && builder.blocks.map(renderBlock)}

    {renderMagazineFeature('beforeProducts')}

    {renderFilteredProductsFallback &&
      renderProducts(filteredProductsFallbackBlock, builder.blocks.length)
    }

    {renderMagazineFeature('afterProducts')}

    {showManagedDescription && renderLegacyDescriptionIntro(true)}

    <ContactModal
      open={contactModalOpen}
      initialSubject={contactModalSubject}
      onClose={() => setContactModalOpen(false)}
    />
  </div>
}

export default LandingPage
